import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence, useMotionValue, animate } from 'framer-motion';
import { Swords, Plus, Bot, X, Check, Loader2, ShieldCheck, Skull, Package, ArrowDownToLine, ArrowUpFromLine, Search, AlertTriangle, Lock, Coins, Sparkles, ExternalLink, Server, CheckCircle2, User as UserIcon, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useChatLayout } from '../context/ChatLayoutContext';

/* ⚠️ EŞYA GÖRSELLERİ ARTIK PAYLAŞILAN MODÜLDEN.
   Bu tanımlar (RARITY, ItemArt, ItemTile…) buradan çıkarıldı çünkü aynıları
   cüzdan penceresindeki çekim envanterinde de kullanılıyor. İki kopya
   tutmak, bir nadirlik rengi eklendiğinde aynı eşyanın iki ekranda farklı
   görünmesi demekti. */
import {
  RARITY, rarityOf, fmtFull, fmtVal, monogram, hueOf, ItemArt, ItemTile,
} from '../components/mm2/ItemVisuals';
/* 🔪 MM2 markası + YATIRMA MODU DEFTERİ.
   Defter, bu sayfanın en sinir bozucu hatasını kapatıyor — açıklaması
   `isWalletDepositSession`'ın kullanıldığı yerde. */
import {
  Mm2Logo, rememberDepositMode, isWalletDepositSession,
} from '../components/mm2/Mm2Brand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const WS_URL = BACKEND_URL.replace(/^http/, 'ws');

/* ============================================================================
   MM2 — EŞYA COINFLIP'İ
   ============================================================================

   MİMARİ NOTU
   -----------
   Bu sayfa FİYAT HESAPLAMAZ. server.py'deki yorum net: "fiyatlar YALNIZCA
   sunucu tarafında — istemciye ASLA güvenilmez". Buradaki her değer
   /api/game/mm2/* uçlarından geldiği gibi GÖSTERİLİR. Toplamları da yalnızca
   kullanıcıya önizleme olsun diye topluyoruz; kabul/ret kararını her zaman
   sunucu verir (±%5 bandı, min/max, kilit çakışması hep orada).

   TARAFLAR
   --------
   Coinflip'in iki yüzü MM2'nin kendi dünyasından: Murderer (bıçak, kızıl) ve
   Sheriff (yıldız, mavi). Kurucunun hangi tarafa düştüğü game.id'den
   TÜRETİLİR (aşağıdaki sideOfGame) — böylece sunucuya alan eklemeden
   izleyen herkes AYNI tarafı görür. Kurucunun tarafı SEÇMESİNİ istiyorsanız
   README'deki 3 satırlık backend yaması var.
   ⚠️ Taraf yalnızca GÖRSELDİR. Kazananı sunucudaki bilet belirler; taraf
      seçiminin kazanma olasılığına HİÇBİR etkisi yoktur.
============================================================================ */

/* ---------------------------------------------------------------- BİÇİMLEME */
// K/M/B kısaltma. Intl'in "compact" desteğine GÜVENMEZ — bazı webview
// ortamları onu yok sayıp düz sayı basıyor (CaseBattles.js'te aynı gerekçe).



const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}sn`;
  if (s < 3600) return `${Math.floor(s / 60)}dk`;
  if (s < 86400) return `${Math.floor(s / 3600)}sa`;
  return `${Math.floor(s / 86400)}g`;
};

/* ------------------------------------------------------------------ NADİRLİK */
// MM2'nin kendi kademeleri. Renkler siteye ait paletle (mor #B059E8 marka)
// çakışmayacak şekilde seçildi: marka moru ARAYÜZ için, bu renkler EŞYA için.
/* -------------------------------------------------------------- TARAF (YÜZ) */
/* ⚠️ TARAF RENKLERİ GÖRSELLERLE AYNI OLMAK ZORUNDA. Bu bir tercih değil:
   ekrandaki taraf göstergesi ile paranın oturduğu yüz birbirini tutmazsa
   oyuncu hangi tarafta olduğunu okuyamıyor. Daha önce sheriff maviydi ve
   para görselinde mavi hiç yoktu — aynı hata.

   Paralar site paletine çevrildi (tails → mor, heads → yeşil), bu yüzden
   buradaki değerler de birlikte güncellendi.

   ⚠️ BİLİNÇLİ BİR ÖDÜN: `base` değerleri artık markanın ARAYÜZ renkleriyle
   aynı (#B059E8 mor, #16A34A yeşil). Eski yorum bunlardan bilerek
   kaçınıyordu — "marka moru arayüz için, bu renkler eşya için". Paralar
   siteye uyacaksa bu çakışma kaçınılmaz. Etkisi şu: bir taraf göstergesi
   ile bir arayüz vurgusu yan yana gelince ikisi aynı renkte olabiliyor.
   Karışıklık yaratırsa `base`'leri bir ton kaydırmak (ör. mor #C98BF0,
   yeşil #2ECF6E) görsellerle bağı koparmadan ayrışma sağlar. */
const SIDES = {
  murderer: { key: 'murderer', label: 'PURPLE', base: '#B059E8', deep: '#3B1656',
              text: '#D9AEF5', img: '/tails.png' },
  sheriff:  { key: 'sheriff',  label: 'GREEN',  base: '#16A34A', deep: '#0A3D1F',
              text: '#7DE3A5', img: '/heads.png' },
};

/* ⚠️ Kurucunun SEÇTİĞİ taraf varsa o kullanılır; yoksa id'den türetilir.
   Türetme sunucudaki _mm2_side_from_id ile AYNI algoritma — ayrışırsa
   lobide bir renk, arenada başka bir renk görünürdü. */
const sideOf = (gameOrId) => {
  if (gameOrId && typeof gameOrId === 'object') {
    const st = gameOrId.creator_side;
    if (st === 'murderer' || st === 'sheriff') return st;
    return sideOfGame(gameOrId.id);
  }
  return sideOfGame(gameOrId);
};

// game.id'den kararlı taraf. Aynı id → her istemcide aynı sonuç.
const sideOfGame = (gameId) => {
  let h = 0;
  const s = String(gameId || '');
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return (Math.abs(h) % 2) === 0 ? 'murderer' : 'sheriff';
};
const otherSide = (k) => (k === 'murderer' ? 'sheriff' : 'murderer');

/* ============================================================================
   EŞYA GÖRSELİ
   ----------------------------------------------------------------------------
   image_url boşsa (katalog görselsiz yüklendiyse) KIRIK GÖRSEL İKONU çıkmaz:
   eşya adından türetilen kararlı bir gradyan + monogram çizilir. Aynı eşya
   her yerde aynı görünür, sıfır ağ isteği.
============================================================================ */
const CoinFace = ({ sideKey, size = 18 }) => (
  <img
    src={SIDES[sideKey]?.img || SIDES.murderer.img}
    alt=""
    width={size}
    height={size}
    draggable={false}
    style={{ display: 'block', width: size, height: size, objectFit: 'contain' }}
  />
);

/* ============================================================================
   MADENİ PARA

   ⚠️⚠️ ANİMASYON REACT'İN DIŞINDA. Açı bir ref üzerinden doğrudan
      `style.transform`'a yazılıyor: hiçbir state güncellenmiyor, hiçbir
      uzlaştırma (reconciliation) çalışmıyor. Daha önce iki yanlış denedim
      ve ikisi de kasıyordu:
        · sprite + setState → her karede React render (48 kez)
        · MotionValue      → framer'ın kendi döngüsü + abonelik yükü
      Doğrudan DOM yazımı bu katmanların hepsini atlıyor. Kalan tek iş,
      GPU'nun zaten yaptığı `rotateY` dönüşümü.

   ⚠️ GÖRSELLER TAM ÇÖZÜNÜRLÜKTE (512px kaynak, 190px'e çizilir) — küçük
      kareleri büyütmek bulanıklaştırıyordu.

   ⚠️ DÖNÜŞ YAVAŞLATILDI: hazırlıkta 2.6 sn/tur, flip'te 4 tur. Hızlı
      dönüş hem okunmuyor hem de her karede daha çok piksel değiştirdiği
      için kompozit yükünü artırıyor.
============================================================================ */
const COIN_SPIN_MS = 2600;      // hazırlık: bir tam tur
const COIN_FLIP_MS = 4600;      // flip: sunucunun 5 sn beklemesinden kısa
const COIN_FLIP_TURNS = 4;

const MM2Coin = React.memo(({ size = 190, phase, creatorWins, creatorSideKey, reduced }) => {
  const el = useRef(null);
  const raf = useRef(0);
  const angle = useRef(0);

  const frontIsCreator = creatorSideKey === 'murderer';
  const landsFront = creatorWins === null || creatorWins === undefined
    ? true
    : (frontIsCreator ? creatorWins : !creatorWins);

  /* Tek yazma noktası — her yerden buradan geçiliyor. */
  const write = (deg) => {
    angle.current = deg;
    const n = el.current;
    if (n) n.style.transform = `rotateY(${deg}deg)`;
  };

  useEffect(() => {
    cancelAnimationFrame(raf.current);

    if (phase === 'starting' && !reduced) {
      const t0 = performance.now(), from = angle.current;
      const tick = (now) => {
        write(from + ((now - t0) / COIN_SPIN_MS) * 360);
        raf.current = requestAnimationFrame(tick);
      };
      raf.current = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf.current);
    }

    if (phase === 'flipping') {
      const face = landsFront ? 0 : 180;
      const from = angle.current;
      if (reduced) { write(face); return; }
      /* Hedef: DAİMA ileri doğru, kazanan yüze tam oturacak şekilde. */
      let to = from + 360 * COIN_FLIP_TURNS;
      to += (((face - (((to % 360) + 360) % 360)) % 360) + 360) % 360;
      const span = to - from, t0 = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / COIN_FLIP_MS);
        /* ⚠️⚠️ HIZ SÜREKLİLİĞİ — EĞRİ ÇÖZÜLEREK BULUNDU.
           Hazır eğriler (`1-(1-p)³` vb.) flip'i ortalamanın 3–7 katı
           hızla başlatıyordu; hazırlık dönüşü 138°/sn giderken flip
           1000°/sn ile devralınca göz bunu "takılma" olarak okuyor.
           İstenen dört koşul: e(0)=0, e(1)=1, e'(0)=a, e'(1)=0.
           Kübik e(p)=a·p+b·p²+c·p³ için çözüm: b=3−2a, c=a−2.
           `a` iki animasyonun hız ORANI, yani flip tam hazırlık hızıyla
           başlıyor ve sonunda tam duruyor. Eğri monoton artan —
           geri sarma olmuyor. */
        const a0 = (360 / (COIN_SPIN_MS / 1000)) / (span / (COIN_FLIP_MS / 1000));
        const e = a0 * p + (3 - 2 * a0) * p * p + (a0 - 2) * p * p * p;
        write(from + span * e);
        if (p < 1) raf.current = requestAnimationFrame(tick);
      };
      raf.current = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf.current);
    }

    if (phase !== 'result') write(0);      // bekleyen/boş durum: duruk
    return () => cancelAnimationFrame(raf.current);
  }, [phase, landsFront, reduced]);

  const glow = phase === 'result'
    ? SIDES[landsFront ? 'murderer' : 'sheriff'].base : '#B059E8';

  /* ⚠️⚠️ HER YÜZ KENDİ KATMANINDA.
     Hazırlık dönüşü akıcıydı ama flip kasıyordu ve fark HIZDA: hazırlık
     138°/sn, flip tepe 494°/sn. Hızlı dönüşte para saniyede birkaç kez
     kenardan geçiyor (izdüşüm genişliği 190px → 0 → 190px) ve tarayıcı
     her seferinde kaynak görseli YENİ ölçekte yeniden örnekliyordu.
     Yavaşken yetişiyor, hızlanınca birikiyordu.
     `translateZ(0)` her yüzü ayrı bir kompozit katmana alıyor: GPU dokuyu
     BİR KEZ hazırlıyor, sonrasında yalnızca dönüştürüyor — yeniden
     örnekleme yok.
     ⚠️ Görseller de 512px'ten 256px'e indirildi: doku dört kat küçüldü,
     yeniden örnekleme gerekse bile dörtte bir maliyetle. */
  const face = {
    position: 'absolute', inset: 0, width: '100%', height: '100%',
    objectFit: 'contain',
    backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
    willChange: 'transform',
  };

  return (
    <div className="relative flex items-center justify-center"
      style={{ width: size, height: size }}>
      <div className="absolute rounded-full pointer-events-none"
        style={{
          width: size * 1.5, height: size * 1.5,
          background: `radial-gradient(circle, ${glow}55 0%, ${glow}1A 45%, transparent 72%)`,
          opacity: phase === 'result' ? 0.55 : 0.24,
          transition: 'opacity .5s', transform: 'translateZ(0)',
        }} />
      <div
        ref={el}
        style={{
          width: size, height: size,
          transformStyle: 'preserve-3d',
          willChange: 'transform',
          transform: 'rotateY(0deg)',
          /* Perspektif 3D bağlamı sabitler; olmadan bazı tarayıcılar
             her karede katman ağacını yeniden değerlendiriyor. */
          perspective: 1200,
        }}
      >
        <img src={SIDES.murderer.img} alt="" draggable={false} decoding="sync"
             style={{ ...face, transform: 'rotateY(0deg) translateZ(0.01px)' }} />
        <img src={SIDES.sheriff.img} alt="" draggable={false} decoding="sync"
             style={{ ...face, transform: 'rotateY(180deg) translateZ(0.01px)' }} />
      </div>
    </div>
  );
});


const Avatar = React.memo(({ username, isBot, size = 46, ring }) => {
  const [failed, setFailed] = useState(false);
  const name = username || '—';

  useEffect(() => { setFailed(false); }, [username]);

  const initials = String(name).replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?';
  const hue = hueOf(name);

  return (
    <div
      className="relative rounded-xl overflow-hidden shrink-0"
      style={{
        width: size, height: size,
        boxShadow: ring ? `0 0 0 2px ${ring}, 0 0 18px -4px ${ring}` : '0 0 0 1px #2A2D31',
      }}
    >
      {isBot ? (
        <div className="w-full h-full bg-[#1A1D20] flex items-center justify-center">
          <Bot className="text-white/55" style={{ width: size * 0.5, height: size * 0.5 }} />
        </div>
      ) : failed || !username ? (
        <div
          className="w-full h-full flex items-center justify-center font-heading font-black text-white/90"
          style={{
            background: `linear-gradient(140deg, hsl(${hue} 44% 30%), hsl(${hue} 40% 15%))`,
            fontSize: size * 0.36,
          }}
        >
          {initials}
        </div>
      ) : (
        <img
          src={`${BACKEND_URL}/api/user/avatar/${encodeURIComponent(username)}`}
          alt={name}
          loading="lazy"
          draggable={false}
          onError={() => setFailed(true)}
          className="w-full h-full object-cover bg-[#1A1D20]"
        />
      )}
    </div>
  );
});

/* ============================================================================
   EŞYA ŞERİDİ — bir tarafın koyduğu eşyalar
============================================================================ */
const ItemStrip = ({ items = [], align = 'center', size = 44, cap = 4 }) => {
  /* ⚠️⚠️ İKİ KURAL:
     1) EN FAZLA `cap` EŞYA — yer olsa bile. Satır bir envanter değil, bir
        özet; dördün ötesi bilgi katmıyor ve satırı okunmaz yapıyor.
     2) SIĞMIYORSA HİÇ ÇİZME. Önceki sürüm `Math.max(1, ...)` ile yer
        olmasa da bir kutu zorluyordu; sonuç iki yanından kırpılmış bir
        parçaydı — hiç göstermemekten kötü. Artık yer yoksa yalnızca
        "+N items" etiketi kalıyor, o da sığmazsa hiçbir şey. */
  const wrapRef = useRef(null);
  const [fit, setFit] = useState(cap);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const GAP = 6, CHIP = 42;          // kutular arası boşluk, "+N" etiketi
    const measure = () => {
      const w = el.clientWidth || 0;
      if (!w) return;
      const wanted = Math.min(cap, items.length);
      // Hepsi (cap kadarı) sığıyor mu?
      if (wanted * size + (wanted - 1) * GAP <= w && items.length <= cap) {
        setFit(wanted);
        return;
      }
      // ⚠️ Etiket bile sığmıyorsa HİÇBİR ŞEY çizme. "+12"yi 30px'e
      //    sıkıştırmak "+1…" gibi anlamsız bir kırpıntı üretiyordu.
      if (w < CHIP) { setFit(-1); return; }
      // Sığmıyorsa "+N" için yer ayır ve kalan alana kaç kutu girer bak.
      const room = w - CHIP - GAP;
      const n = room <= 0 ? 0 : Math.floor((room + GAP) / (size + GAP));
      setFit(Math.max(0, Math.min(cap, n)));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [items.length, size, cap]);

  if (!items?.length || fit < 0) return null;

  const shown = items.slice(0, fit);
  const rest = items.length - shown.length;

  return (
    <div
      ref={wrapRef}
      className={`flex items-center gap-1.5 w-full overflow-hidden ${
        align === 'right' ? 'justify-end' : align === 'left' ? 'justify-start' : 'justify-center'
      }`}
    >
      {shown.map((it, i) => {
        const rar = rarityOf(it.rarity);
        return (
          <div
            key={`${it.item_id}-${i}`}
            title={it.name}
            className="rounded-lg bg-[#15171a] border p-1 shrink-0"
            style={{ width: size, height: size, borderColor: `${rar.base}2E` }}
          >
            <ItemArt item={it} />
          </div>
        );
      })}
      {rest > 0 && (
        <span className="text-[11px] font-semibold text-white/40 whitespace-nowrap shrink-0 pl-0.5">
          +{rest}
        </span>
      )}
    </div>
  );
};
/* ============================================================================
   EŞYA IZGARASI — arena/sonuç ekranında, fiyatlarıyla

   ⚠️ Lobideki ItemStrip'ten AYRI: orada yer dar olduğu için yalnızca görsel
      var. Burada kullanıcı kararını gözden geçiriyor ve neyin ne ettiğini
      görmek istiyor; fiyatsız bir ızgara "toplam nereden çıktı" sorusunu
      cevapsız bırakıyordu.
============================================================================ */
const ItemGrid = ({ items = [], align = 'left' }) => {
  if (!items.length) {
    return (
      <p className={`text-[11px] text-white/25 ${align === 'right' ? 'text-right' : ''}`}>
        No items
      </p>
    );
  }
  return (
    <div
      className={`grid gap-1.5 ${align === 'right' ? 'justify-items-end' : ''}`}
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(58px, 1fr))' }}
    >
      {items.map((it, i) => {
        const rar = rarityOf(it.rarity);
        return (
          <div
            key={`${it.item_id}-${i}`}
            title={it.name}
            className="rounded-lg bg-[#15171a] border border-[#2A2D31] p-1.5 flex flex-col items-center gap-1"
            style={{ borderColor: `${rar.base}33` }}
          >
            <div className="w-9 h-9"><ItemArt item={it} /></div>
            <span className="text-[9px] font-bold text-white/70 tabular-nums leading-none">
              {fmtVal(it.value)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/* ============================================================================
   FLIP ARENASI — büyük sahne
============================================================================ */
const FlipArena = ({ live, meId, onClose, reduced }) => {
  const g = live?.game;

  // ⚠️ KANCALAR ERKEN return'DEN ÖNCE. `if (!g) return null` yukarıda olsaydı
  //    live bir an için boşaldığında React "beklenenden az kanca" hatası verip
  //    ağacı düşürürdü. Kanca sırası her render'da AYNI olmak zorunda.
  const [ticketShown, setTicketShown] = useState(0);
  const phase = live?.phase;
  const ticket = live?.ticket;
  const ticketSpace = live?.ticket_space;

  // ⚠️ Paranın 3B geometrisi SAYISAL boyut ister (yarıçap, kalınlık, kenar
  //    dilimi yüksekliği hepsi buradan türüyor) — CSS ile ölçeklenemez.
  //    Doğrudan window.innerWidth okumak, telefonu çeviren kullanıcıda
  //    boyutu donduruyordu; dinleyiciyle güncel tutuluyor.
  /* ⚠️ Sayaç Date.now() FARKINDAN hesaplanır, azaltılarak değil: sekme arka
     plana alınıp geri gelse bile doğru kalır. Başlangıç anı, oyun listeye
     düştüğü anda işaretleniyor (live.startedAt) — yoksa arenayı geç açan
     izleyici 10'dan başlardı ve katılanla farklı sayı görürdü. */
  const startedAt = live?.startedAt || 0;
  const total = live?.countdown || 10;
  const [secsLeft, setSecsLeft] = useState(() =>
    startedAt ? Math.max(0, total - Math.floor((Date.now() - startedAt) / 1000)) : total
  );
  useEffect(() => {
    if (phase !== 'starting' || !startedAt) return;
    const tick = () =>
      setSecsLeft(Math.max(0, total - Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [phase, startedAt, total]);

  const [coinSize, setCoinSize] = useState(232);
  useEffect(() => {
    const measure = () => setCoinSize(window.innerWidth < 640 ? 200 : 300);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    if (phase !== 'flipping' || ticket == null) return;
    if (reduced) { setTicketShown(ticket); return; }
    const start = performance.now();
    const dur = 4400;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      // son %15'te gerçek değere kilitlenir; öncesi "arıyor" hissi verir
      setTicketShown(p > 0.85 ? ticket : Math.floor(Math.random() * (ticketSpace || 1_000_000)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, ticket, ticketSpace, reduced]);

  if (!g) return null;

  const creatorSideKey = sideOfGame(g.id);
  const joinerSideKey = otherSide(creatorSideKey);
  const cSide = SIDES[creatorSideKey];
  const jSide = SIDES[joinerSideKey];

  const creatorWins = live.creator_wins ?? null;
  const winnerSide = creatorWins === null ? null : (creatorWins ? creatorSideKey : joinerSideKey);
  const iAmCreator = meId && g.creator_id === meId;
  const iAmJoiner = meId && g.joiner_id === meId;
  const iWon = phase === 'result' && live.winner_id && meId && live.winner_id === meId;
  const iLost = phase === 'result' && (iAmCreator || iAmJoiner) && !iWon;

  const Side = ({ side, username, robloxUsername, isBot, value, chance, items, won, mine, alignRight }) => (
    <div className={`flex-1 min-w-0 ${alignRight ? 'md:text-right' : ''}`}>
      <div className={`flex items-center gap-3 mb-3 ${alignRight ? 'md:flex-row-reverse' : ''}`}>
        {/* ⚠️ Avatarın ALT KÖŞESİNDE taraf madeni. Kimin hangi tarafta
            olduğunu okumak için ada değil, simgeye bakılıyor — madeni
            dönerken hangi yüzün kimi temsil ettiği anında anlaşılsın.
            Sol taraf sol alta, sağ taraf sağ alta: iki taraf birbirine
            bakıyormuş gibi duruyor. */}
        <div className="relative shrink-0">
          <Avatar
            username={username}
            isBot={isBot}
            size={62}
            ring={phase === 'result' ? (won ? side.base : '#2A2D31') : side.base}
          />
          <div
            className={`absolute -bottom-1.5 ${alignRight ? '-right-1.5' : '-left-1.5'} rounded-full ring-2 ring-[#121417]`}
            style={{ lineHeight: 0 }}
          >
            <CoinFace sideKey={side.key} size={28} />
          </div>
        </div>
        <div className="min-w-0">
          <div className={`flex items-center gap-1.5 ${alignRight ? 'md:justify-end' : ''}`}>
            <p className="font-bold text-white text-sm truncate">{username || 'Waiting…'}</p>
            {mine && (
              <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#B059E8]/20 text-[#c98bf0]">
                you
              </span>
            )}
          </div>
          <div className={`flex items-center gap-1.5 mt-0.5 ${alignRight ? 'md:justify-end' : ''}`}>
            {side.key === 'sheriff'
              ? <ShieldCheck className="w-3 h-3" style={{ color: side.base }} />
              : <Skull className="w-3 h-3" style={{ color: side.base }} />}
            <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: side.text }}>
              {side.label}
            </span>
          </div>
        </div>
      </div>

      <div className={`flex items-baseline gap-2 mb-2.5 ${alignRight ? 'md:justify-end' : ''}`}>
        <span className="font-heading font-black text-2xl text-white">
          {fmtVal(value)}
          {/* ⚠️ Birim simgesi tutarın YANINDA: değerin para olduğu tek
              bakışta anlaşılmalı, yoksa eşya sayısı sanılabiliyor. */}
          <span className="text-sm text-white/35 ml-0.5">$</span>
        </span>
        <span className="text-xs font-bold" style={{ color: side.text }}>
          {(chance ?? 50)}% chance
        </span>
      </div>

      <ItemGrid items={items} align={alignRight ? 'right' : 'left'} />
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6"
      style={{ background: 'rgba(6,7,9,.86)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.94, y: 18 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.96, y: 10 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl rounded-2xl border border-[#2A2D31] bg-[#0E0F11] overflow-hidden"
        style={{
          boxShadow: winnerSide
            ? `0 0 0 1px ${SIDES[winnerSide].base}55, 0 40px 90px -30px ${SIDES[winnerSide].base}55`
            : '0 40px 90px -30px rgba(0,0,0,.9)',
        }}
      >
        {/* iki tarafın renk zemini */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-y-0 left-0 w-1/2"
            style={{ background: `linear-gradient(to right, ${cSide.base}1c, transparent 75%)` }} />
          <div className="absolute inset-y-0 right-0 w-1/2"
            style={{ background: `linear-gradient(to left, ${jSide.base}1c, transparent 75%)` }} />
        </div>

        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-lg bg-[#15171a] border border-[#2A2D31] flex items-center justify-center text-white/60 hover:text-white hover:border-[#3c4046] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative p-5 sm:p-8">
          {/* üst şerit: pot + durum */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="px-4 py-1.5 rounded-full bg-[#15171a] border border-[#2A2D31] flex items-center gap-2">
              <Coins className="w-3.5 h-3.5 text-[#B059E8]" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">Pot</span>
              <span className="font-heading font-black text-white text-sm">
                {fmtVal((g.creator_value || 0) + (g.joiner_value || 0))}
              </span>
            </div>
          </div>

          {/* sahne */}
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-4">
            <Side
              side={cSide}
              username={g.creator_username}
              robloxUsername={g.creator_roblox_username}
              value={g.creator_value}
              chance={g.creator_chance}
              items={g.creator_items}
              won={creatorWins === true}
              mine={iAmCreator}
            />

            <div className="flex flex-col items-center gap-3 shrink-0">
              <MM2Coin
                size={coinSize}
                phase={phase}
                creatorWins={creatorWins}
                creatorSideKey={creatorSideKey}
                reduced={reduced}
              />

              {/* durum yazısı */}
              <div className="h-14 flex flex-col items-center justify-center text-center">
                <AnimatePresence mode="wait">
                  {phase === 'starting' && (
                    <motion.div key="s" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                      className="flex flex-col items-center gap-0.5">
                      {/* ⚠️ AYNI SAYAÇ LOBİ SATIRINDA DA VAR. İkisi de sunucunun
                          gönderdiği başlangıç anından türetiliyor; ayrı ayrı
                          saymak ikisinin ayrışmasına ve "hangisi doğru"
                          sorusuna yol açardı. */}
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">
                        Flipping in
                      </p>
                      <p className="font-heading font-black text-3xl text-white tabular-nums leading-none">
                        {secsLeft}
                      </p>
                    </motion.div>
                  )}
                  {phase === 'flipping' && (
                    <motion.div key="f" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                      className="flex flex-col items-center gap-1">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">Ticket</p>
                      <p className="font-heading font-black text-lg text-white tabular-nums">
                        {String(ticketShown).padStart(6, '0')}
                      </p>
                    </motion.div>
                  )}
                  {phase === 'result' && winnerSide && (
                    <motion.div key="r" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                      className="flex flex-col items-center gap-0.5">
                      <p className="font-heading font-black text-xl leading-none"
                        style={{ color: SIDES[winnerSide].base }}>
                        {SIDES[winnerSide].label}
                      </p>
                      <p className="text-[11px] font-bold text-white/70">
                        {live.winner_username} wins
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <Side
              side={jSide}
              username={g.joiner_username}
              robloxUsername={g.joiner_roblox_username}
              isBot={g.joiner_is_bot}
              value={g.joiner_value}
              chance={g.joiner_chance}
              items={g.joiner_items}
              won={creatorWins === false}
              mine={iAmJoiner}
              alignRight
            />
          </div>

          {/* kişisel sonuç bandı */}
          <AnimatePresence>
            {(iWon || iLost) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 rounded-xl px-4 py-3 flex items-center justify-center gap-2 border"
                style={{
                  borderColor: iWon ? '#16A34A55' : '#E5484D44',
                  background: iWon ? 'rgba(22,163,74,.1)' : 'rgba(229,72,77,.08)',
                }}
              >
                {iWon ? <Sparkles className="w-4 h-4 text-[#2ecf6e]" /> : <X className="w-4 h-4 text-[#E5484D]" />}
                <span className="text-sm font-bold" style={{ color: iWon ? '#2ecf6e' : '#FF9C9E' }}>
                  {iWon
                    ? `${live.items_won?.length || 0} items won — added to your inventory`
                    : 'Not this time'}
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* adillik ispatı */}
          <div className="mt-6 pt-4 border-t border-[#2A2D31]">
            <div className="flex items-start gap-2 text-[10px] text-white/35 mm-num break-all leading-relaxed">
              <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-px text-white/25" />
              <div className="min-w-0">
                <p><span className="text-white/50">hash:</span> {g.server_seed_hash || '—'}</p>
                {live.server_seed && (
                  <p className="mt-0.5"><span className="text-white/50">seed:</span> {live.server_seed}</p>
                )}
                {live.ticket != null && (
                  <p className="mt-0.5">
                    <span className="text-white/50">bilet:</span> {live.ticket} / {live.ticket_space}
                    {' · '}
                    <span className="text-white/50">threshold:</span> {Math.round((g.creator_chance || 50) * 10_000)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ============================================================================
   EŞYA SEÇME MODALI  —  create / join / withdraw ÜÇÜ İÇİN TEK BİLEŞEN
   ============================================================================
   Üç akış da aynı işi yapıyor: envanterden eşya seç, toplamı gör, gönder.
   Ayrı ayrı yazılsaydı üç kopya zamanla ayrışırdı (CaseBattles.js'teki
   "altı dosyada altı kopya" notunun aynısı). Farklılıklar tek yerde,
   `mode`'a bakan üç küçük kuralda toplandı:

     create   → en fazla config.max_items_per_side, değer min..max arası
     join     → değer ±%5 BANDINDA olmalı (game.min/max_join_value)
     withdraw → en fazla config.max_items_per_withdraw (VARSAYILAN 4)

   ⚠️ Buradaki kontroller yalnızca KULLANICIYA ANINDA GERİ BİLDİRİM içindir.
      Gerçek kararı her zaman sunucu verir; bu ekran sunucuyu taklit eder,
      YERİNE GEÇMEZ.
============================================================================ */
/* ⚠️ Modalın İÇİNDE tanımlıydı: her render'da yeni tip → iki madeni para
   görseli baştan yükleniyordu. Envanterde bir eşya seçmek bile bunu
   tetikliyordu (state değişimi modalı yeniden çiziyor). */
const SidePicker = React.memo(({ side, setSide }) => (
    <div className="flex items-center justify-center gap-8 sm:gap-14">
      {['murderer', 'sheriff'].map((k) => {
        const sd = SIDES[k];
        const on = side === k;
        return (
          <button
            key={k}
            type="button"
            onClick={() => setSide(k)}
            aria-pressed={on}
            aria-label={sd.label}
            className="relative rounded-full transition-transform duration-200 focus:outline-none"
            style={{ transform: on ? 'scale(1)' : 'scale(0.86)' }}
          >
            <span
              className="absolute -inset-1.5 rounded-full transition-opacity duration-200"
              style={{
                opacity: on ? 1 : 0,
                boxShadow: `0 0 0 2px ${sd.base}, 0 0 22px -4px ${sd.base}`,
              }}
            />
            <span
              className="block relative transition-opacity duration-200"
              style={{ opacity: on ? 1 : 0.4, lineHeight: 0 }}
            >
              <CoinFace sideKey={k} size={72} />
            </span>
          </button>
        );
      })}
    </div>
));

const ItemPickerModal = ({
  open, mode, inventory, config, game, busy, profileName, onClose, onSubmit,
}) => {
  const [sel, setSel] = useState([]);
  /* ⚠️ Taraf seçimi YALNIZCA görseldir. Kazanan bilet/şansla hesaplanıyor
     ve o hesaba taraf hiç girmiyor — aksi hâlde oyuncu tarafını seçerek
     şansını değiştirebilirdi. */
  const [side, setSide] = useState('murderer');
  const [rbx, setRbx] = useState('');           // seçili envanter id'leri
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('value_desc');
  const [rarity, setRarity] = useState('all');

  const maxItems = mode === 'withdraw'
    ? (config?.max_items_per_withdraw ?? 4)
    : (config?.max_items_per_side ?? 20);

  useEffect(() => {
    if (open) {
      setSel([]); setQ(''); setRarity('all'); setRbx(profileName || '');
      setSide('murderer');
    }
  }, [open, mode, game?.id, profileName]);

  // yalnızca serbest eşyalar seçilebilir
  const available = useMemo(
    () => (inventory || []).filter((i) => i.state === 'available'),
    [inventory]
  );

  const rarities = useMemo(() => {
    const set = new Set(available.map((i) => String(i.rarity || 'common').toLowerCase()));
    // MM2'nin kendi kademe sırası. Önceki karşılaştırıcı `a`'ya hiç bakmıyordu
    // ve tutarsız sonuç üretiyordu (bazı motorlarda sıralamayı bozar).
    const order = ['ancient', 'unique', 'chroma', 'godly', 'legendary', 'legend',
                   'rare', 'uncommon', 'common', 'vintage', 'pet'];
    const rank = (r) => {
      const i = order.indexOf(r);
      return i === -1 ? order.length : i;      // bilinmeyen kademe en sona
    };
    return ['all', ...Array.from(set).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))];
  }, [available]);

  const shown = useMemo(() => {
    let list = available;
    if (q.trim()) {
      const t = q.trim().toLowerCase();
      list = list.filter((i) => String(i.name || '').toLowerCase().includes(t));
    }
    if (rarity !== 'all') {
      list = list.filter((i) => String(i.rarity || 'common').toLowerCase() === rarity);
    }
    const c = [...list];
    if (sort === 'value_desc') c.sort((a, b) => b.value - a.value);
    else if (sort === 'value_asc') c.sort((a, b) => a.value - b.value);
    else c.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return c;
  }, [available, q, rarity, sort]);

  const byId = useMemo(() => {
    const m = new Map();
    available.forEach((i) => m.set(i.id, i));
    return m;
  }, [available]);

  /* ⚠️⚠️ KADEMELİ ÇİZİM — AÇILIŞ ANİMASYONU BLOKLANMASIN.
     Envanterin tamamı tek karede çiziliyordu: 200 eşyalı bir hesapta 200
     kutu + 200 görsel, hepsi modal açılırken. Tarayıcı o karede takılıyor
     ve pencere "kasarak" açılıyordu.
     İlk parti hemen çiziliyor (ekranı doldurmaya yeter), gerisi açılış
     animasyonu bittikten SONRA ve kaydırdıkça ekleniyor. Kullanıcı farkı
     görmüyor çünkü ilk ekran zaten dolu. */
  /* ⚠️ İLK PARTİ KÜÇÜK. Açılış animasyonu backdrop-filter: blur() ile
     birlikte çalışıyor ve o efekt her karede arkadaki tüm sayfayı yeniden
     bulanıklaştırıyor — en pahalı CSS işlemlerinden biri. Aynı karede 48
     kutu çizmek bunun üstüne binince takılma görünür oluyordu.
     İlk ekranı dolduracak kadarı (18) hemen, gerisi animasyon BİTTİKTEN
     sonra geliyor. Kullanıcı farkı görmüyor; ilk ekran zaten dolu. */
  const FIRST = 18;
  const PAGE = 36;
  const [limit, setLimit] = useState(FIRST);
  useEffect(() => { setLimit(FIRST); }, [q, rarity, sort, open]);
  useEffect(() => {
    if (!open || limit >= shown.length) return;
    /* ⚠️ requestAnimationFrame + timeout: tarayıcı önce açılış karesini
       bitiriyor, büyütme ondan SONRA sıraya giriyor. Doğrudan setTimeout
       aynı kareye düşebiliyordu. */
    const t = setTimeout(() => {
      requestAnimationFrame(() =>
        setLimit((n) => Math.min(shown.length, n + PAGE)));
    }, limit === FIRST ? 360 : 80);
    return () => clearTimeout(t);
  }, [open, limit, shown.length]);

  const visible = useMemo(() => shown.slice(0, limit), [shown, limit]);

  /* Kaydırma sonuna yaklaşınca bir parti daha. */
  const onGridScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) {
      setLimit((n) => (n >= shown.length ? n : n + PAGE));
    }
  }, [shown.length]);

  const total = useMemo(
    () => sel.reduce((s, id) => s + (byId.get(id)?.value || 0), 0),
    [sel, byId]
  );

  const toggle = useCallback((id) => {
    setSel((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= maxItems) {
        toast.error(
          mode === 'withdraw'
            ? `You can withdraw at most ${maxItems} items at once.`
            : `You can put up at most ${maxItems} items per side.`
        );
        return prev;
      }
      return [...prev, id];
    });
  }, [maxItems, mode]);

  /* ---------------- JOIN: ±%5 bandı ---------------- */
  const band = mode === 'join' && game
    ? { min: game.min_join_value || 0, max: game.max_join_value || 0 }
    : null;
  const inBand = band ? total >= band.min && total <= band.max : true;

  // Bandı tutturan bir küme bul. Açgözlü + yeniden başlatmalı: her eşyadan
  // başlayarak dener, banda giren kümeler içinde ORTAYA en yakınını seçer.
  // Envanterler küçük (≤ birkaç yüz) olduğu için O(n²) fazlasıyla yeterli.
  const autoPick = useCallback(() => {
    if (!band) return;
    const pool = [...available].sort((a, b) => b.value - a.value);
    const target = (band.min + band.max) / 2;
    let best = null;

    for (let start = 0; start < pool.length; start++) {
      const pick = [];
      let sum = 0;
      for (let k = 0; k < pool.length; k++) {
        const it = pool[(start + k) % pool.length];
        if (pick.length >= maxItems) break;
        if (sum + it.value <= band.max) { pick.push(it.id); sum += it.value; }
      }
      if (sum >= band.min && sum <= band.max) {
        const score = Math.abs(sum - target);
        if (!best || score < best.score) best = { ids: pick, sum, score };
      }
    }

    if (!best) {
      toast.error('No combination of your items lands in this range. Try a different coinflip.');
      return;
    }
    setSel(best.ids);
    toast.success(`Picked ${best.ids.length} items — ${fmtFull(best.sum)}`);
  }, [available, band, maxItems]);

  /* ---------------- gönderim engelleri ---------------- */
  const blocker = useMemo(() => {
    if (!sel.length) return 'Select items first';
    if (mode === 'join' && band && !inBand) {
      return total < band.min
        ? `Add ${fmtFull(band.min - total)} more`
        : `${fmtFull(total - band.max)} over`;
    }
    if (mode === 'create' && config) {
      if (total < (config.min_value ?? 1)) return `Put up at least ${fmtFull(config.min_value)}`;
      if (total > (config.max_value ?? Infinity)) return `You can put up at most ${fmtFull(config.max_value)}`;
    }
    return null;
  }, [sel, mode, band, inBand, total, config, rbx]);

  if (!open) return null;

  const title = mode === 'create' ? 'Create coinflip'
    : mode === 'join' ? 'Join coinflip'
    : 'Withdraw items';
  const cta = mode === 'create' ? 'Open coinflip'
    : mode === 'join' ? 'Join and flip'
    : 'Start withdrawal';

  /* ⚠️ YAZI YOK — madeni paranın kendisi seçenektir. "RED / BLACK" yazmak
     hem gereksiz (görsel zaten söylüyor) hem de iki dil arasında çeviri
     yükü yaratıyordu. Seçili olan tam opaklıkta ve renkli halkayla; seçili
     olmayan soluk ve halkasız — durum tek bakışta okunuyor. */
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
      /* ⚠️ blur 8px → 4px: bulanıklık yarıya inince tarayıcının her
         karede işlediği piksel alanı da yarıya iniyor. Görsel fark
         neredeyse yok, açılış belirgin şekilde akıcılaşıyor. */
      style={{ background: 'rgba(6,7,9,.86)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 30, scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[92vh] sm:max-h-[86vh] rounded-t-2xl sm:rounded-2xl border border-[#2A2D31] bg-[#0E0F11] flex flex-col overflow-hidden"
        style={{ boxShadow: '0 40px 90px -30px rgba(0,0,0,.95)' }}
      >
        {/* ------- başlık ------- */}
        <div className="px-5 py-4 border-b border-[#2A2D31] flex items-center justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h3 className="font-heading font-black text-lg text-white truncate">{title}</h3>
            <p className="text-[11px] text-white/40 mt-0.5">
              {mode === 'withdraw'
                ? `Up to ${maxItems} items`
                : mode === 'join'
                  ? `Opponent put up ${fmtFull(game?.creator_value)} · stay within ±${config?.join_tolerance_percent ?? 5}%`
                  : `Up to ${maxItems} items`}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-9 h-9 rounded-lg bg-[#15171a] border border-[#2A2D31] flex items-center justify-center text-white/60 hover:text-white hover:border-[#3c4046] transition-colors shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ------- filtreler ------- */}
        <div className="px-5 py-3 border-b border-[#2A2D31] flex flex-wrap items-center gap-2 shrink-0">
          <div className="relative flex-1 min-w-[150px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search items"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#15171a] border border-[#2A2D31] text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#B059E8]/60 transition-colors"
            />
          </div>
          <select
            value={sort} onChange={(e) => setSort(e.target.value)}
            aria-label="Sort"
            className="px-3 py-2 rounded-lg bg-[#15171a] border border-[#2A2D31] text-xs font-semibold text-white/80 focus:outline-none focus:border-[#B059E8]/60"
          >
            <option value="value_desc">Value ↓</option>
            <option value="value_asc">Value ↑</option>
            <option value="name">Name</option>
          </select>
          <select
            value={rarity} onChange={(e) => setRarity(e.target.value)}
            aria-label="Rarity"
            className="px-3 py-2 rounded-lg bg-[#15171a] border border-[#2A2D31] text-xs font-semibold text-white/80 focus:outline-none focus:border-[#B059E8]/60"
          >
            {rarities.map((r) => (
              <option key={r} value={r}>{r === 'all' ? 'All' : (RARITY[r]?.label || r)}</option>
            ))}
          </select>
          {mode === 'join' && (
            <button
              onClick={autoPick}
              className="px-3 py-2 rounded-lg bg-[#B059E8]/15 border border-[#B059E8]/40 text-xs font-bold text-[#c98bf0] hover:bg-[#B059E8]/25 transition-colors"
            >
              Auto pick
            </button>
          )}
          {sel.length > 0 && (
            <button
              onClick={() => setSel([])}
              className="px-3 py-2 rounded-lg bg-[#15171a] border border-[#2A2D31] text-xs font-bold text-white/60 hover:text-white transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        {/* ------- ızgara ------- */}
        {/* ⚠️ onScroll ile kademeli büyütme: sona yaklaşınca bir parti daha
            eşya çiziliyor. Hepsini baştan çizmek modal açılışını kasıyordu. */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0" onScroll={onGridScroll}>
          {shown.length === 0 ? (
            <div className="h-full min-h-[180px] flex flex-col items-center justify-center text-center gap-2">
              <Package className="w-8 h-8 text-white/15" />
              <p className="text-sm font-semibold text-white/50">
                {available.length === 0 ? 'Your inventory is empty' : 'No items match this filter'}
              </p>
              <p className="text-xs text-white/30">
                {available.length === 0
                  ? ''
                  : 'Try a different search or rarity filter.'}
              </p>
            </div>
          ) : (
            /* ⚠️ SÜTUN SAYISI AZALTILDI: 6 yerine 4. Dar kutularda ad
               sığmıyor ve görsel tanınmıyordu. Az sütun, geniş kutu. */
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
              {visible.map((it) => (
                <ItemTile
                  key={it.id}
                  item={it}
                  selected={sel.includes(it.id)}
                  onClick={toggle}
                  compact
                />
              ))}
            </div>
          )}
        </div>

        {/* ------- alt bar ------- */}
        <div className="px-5 py-4 border-t border-[#2A2D31] bg-[#0b0c0e] shrink-0">
          {mode === 'withdraw' && (
            <div className="mb-3">
              <RobloxField value={rbx} onChange={setRbx}
                profileName={profileName} disabled={busy} />
            </div>
          )}
          {mode === 'create' && (
            <div className="mb-5 pb-5 border-b border-[#2A2D31]">
              <SidePicker side={side} setSide={setSide} />
            </div>
          )}

          {/* join bandı göstergesi */}
          {mode === 'join' && band && (
            <div className="mb-3">
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider mb-1.5">
                <span className="text-white/40">{fmtVal(band.min)}</span>
                <span className={inBand ? 'text-[#2ecf6e]' : 'text-[#FF9C9E]'}>
                  {inBand ? 'in range' : 'out of range'}
                </span>
                <span className="text-white/40">{fmtVal(band.max)}</span>
              </div>
              <div className="relative h-2 rounded-full bg-[#15171a] border border-[#2A2D31] overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-[#16A34A]/25" style={{ width: '100%' }} />
                <motion.div
                  className="absolute inset-y-0 w-1 rounded-full"
                  style={{ background: inBand ? '#2ecf6e' : '#E5484D' }}
                  animate={{
                    left: `${Math.max(0, Math.min(100,
                      band.max > 0 ? (total / (band.max * 1.12)) * 100 : 0))}%`,
                  }}
                  transition={{ type: 'spring', stiffness: 260, damping: 30 }}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">
                Selected
                <span className={`ml-1.5 ${sel.length >= maxItems ? 'text-[#F5A524]' : 'text-white/60'}`}>
                  {sel.length}/{maxItems}
                </span>
              </p>
              <p className="font-heading font-black text-xl text-white leading-tight">
                {fmtFull(total)}
              </p>
            </div>

            <button
              onClick={() => onSubmit(sel, rbx.trim(), side)}
              disabled={!!blocker || busy}
              className={`px-6 py-3 rounded-xl font-heading font-black text-sm uppercase tracking-wider transition-all flex items-center gap-2 shrink-0
                ${blocker || busy
                  ? 'bg-[#1A1D20] text-white/30 cursor-not-allowed border border-[#2A2D31]'
                  : 'bg-[#B059E8] text-white hover:bg-[#c176f0] hover:-translate-y-0.5 shadow-[0_10px_28px_-10px_rgba(176,89,232,.8)]'}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {busy ? 'Sending' : (blocker || cta)}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};




/* ============================================================================
   GEÇMİŞ ŞERİDİ — son biten coinflip'ler

   ⚠️ Taraf rengi sideOfGame(id) ile türetilir; lobideki madeni parayla AYNI
      fonksiyon. Ayrı bir alan taşımak ikisinin ayrışmasına ve şeridin yanlış
      renk göstermesine yol açardı.
============================================================================ */
const RecentStrip = ({ games = [] }) => {
  if (!games.length) return null;
  return (
    <div className="flex items-center gap-1.5 min-w-0 overflow-hidden mm2-scroll [mask-image:linear-gradient(to_right,white_88%,transparent_100%)]">
      {games.slice(0, 10).map((g) => {
        /* Kazanan tarafın madeni yüzü — nokta yerine paranın kendisi.
           Sonuç şeridi ile arenadaki para aynı görseli kullanıyor. */
        const key = g.creator_won ? sideOf(g) : otherSide(sideOf(g));
        return (
          <div
            key={g.id}
            title={`${g.winner} — ${fmtVal(g.pot)}${g.vs_bot ? ' (bot)' : ''}`}
            className="shrink-0 opacity-90 hover:opacity-100 transition-opacity cursor-help"
          >
            <CoinFace sideKey={key} size={26} />
          </div>
        );
      })}
    </div>
  );
};

/* ============================================================================
   SONUÇ PENCERESİ — bitmiş bir coinflip'in son hali

   ⚠️ Kazanan/kaybeden ayrımı YEŞİL/KIRMIZI ile yapılıyor, taraf rengiyle
      değil. Taraf rengi (sheriff/murderer) kimin hangi tarafta olduğunu
      söyler; sonucu okumak için gereken şey ise "kim kazandı" — ikisini
      aynı renkle anlatmak, bakan kişiyi bir an düşündürüyordu.
============================================================================ */
/* ⚠️ TEK İZLEME EKRANI — canlı ve bitmiş coinflip aynı bileşende.
   Tek fark: canlı oyunda geri sayım gösteriliyor. */
/* Boyut sınıfları — modül seviyesinde SABİT nesneler. Her ölçümde
   yenisini üretmek memo'yu bozuyordu. */
const MM2_DIMS = {
  sm: { coin: 116, av: 66, badge: 28 },
  md: { coin: 140, av: 80, badge: 34 },
  lg: { coin: 190, av: 104, badge: 44 },
};

/* ⚠️ HeadCol ile aynı gerekçe: içeride tanımlıydı, her render'da tip
   değişiyordu ve eşya ızgarası komple yeniden kuruluyordu. */
const StatCol = React.memo(({ side, value, chance, items, won, done, right }) => (
    <div className="flex-1 min-w-0">
      <div
        className="rounded-lg border px-3 py-2 flex items-center justify-between gap-2 transition-colors"
        style={{
          borderColor: done && won ? `${side.base}66` : '#2A2D31',
          background: done && won ? `${side.base}14` : '#0E0F11',
        }}
      >
        <span className="font-heading font-black text-white text-[15px] tabular-nums">
          {fmtVal(value)}<span className="text-[11px] text-white/35 ml-0.5">$</span>
        </span>
        {items ? (
          <span className="text-[11px] font-bold text-white/40">{items.length} items</span>
        ) : null}
        <span className="text-[11px] font-black tabular-nums" style={{ color: side.text }}>
          {chance}%
        </span>
      </div>
      <div className="mt-2.5">
        <ItemGrid items={items || []} align={right ? 'right' : 'left'} />
      </div>
    </div>
));

/* ⚠️⚠️ BİLEŞEN DIŞARIDA TANIMLANMALI.
   Bu bileşen ResultModal'ın İÇİNDE tanımlıydı. React her render'da yeni
   bir fonksiyon nesnesi görüyor ve onu FARKLI BİR BİLEŞEN TİPİ sayıyor:
   eski ağacı söküp yenisini kuruyor. Sonuç, dönüş bitip faz 'result'a
   geçtiğinde avatarların ve eşya görsellerinin baştan yüklenmesi —
   ekranda gözle görülen "gidip gelme" buydu.
   Dışarıda tanımlanınca tip sabit kalıyor, React yalnızca değişen
   nitelikleri güncelliyor ve hiçbir görsel yeniden yüklenmiyor. */
const HeadCol = React.memo(({ side, name, won, done, right, empty,
                             dims, canJoin, onJoin }) => (
    <div className="flex flex-col items-center min-w-0 w-[86px] sm:w-[150px] shrink-0">
      {/* Boş taraf: rakip yok — gri avatar yerine doğrudan katıl çağrısı. */}
      {empty ? (
        <button
          onClick={canJoin ? onJoin : undefined}
          disabled={!canJoin}
          className="rounded-full border-2 border-dashed flex flex-col items-center justify-center transition-all hover:scale-105 disabled:hover:scale-100 disabled:opacity-45"
          style={{ width: dims.av, height: dims.av, borderColor: `${side.base}66` }}
        >
          <Swords className="w-5 h-5" style={{ color: side.base }} />
        </button>
      ) : (
      <div className="relative shrink-0">
        <Avatar username={name} size={dims.av} ring={done && won ? side.base : '#2A2D31'} />
        <div
          className={`absolute -bottom-2 ${right ? '-right-2' : '-left-2'} rounded-full ring-2 ring-[#121417]`}
          style={{ lineHeight: 0 }}
        >
          <CoinFace sideKey={side.key} size={dims.badge} />
        </div>
      </div>
      )}
      {empty ? (
        <button
          onClick={canJoin ? onJoin : undefined}
          disabled={!canJoin}
          className="mt-3 px-4 py-2 rounded-lg font-heading font-black text-[11px] uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:translate-y-0"
          style={{ background: side.base }}
        >
          {canJoin ? 'Join' : 'Waiting'}
        </button>
      ) : (
        <p className="font-bold text-white text-[13px] truncate max-w-full mt-3 text-center">
          {name}
        </p>
      )}
    </div>
));

const ResultModal = ({ game, live, waiting, canJoin, onJoin, onClose }) => {
  /* ⚠️ Sonuç ÖNCE GİZLİ: modal açılınca para döner ve kazanana oturur.
     Sonucu baştan göstermek "view" eylemini tablo okumaya indirgerdi. */
  /* Bitmiş oyunda dönüş modal açılınca TEKRAR oynatılır; canlı oyunda faz
     sunucudan gelir (herkes aynı anı görüyor). */
  const [replay, setReplay] = useState('flipping');
  useEffect(() => {
    if (live) return;
    setReplay('flipping');
    const t = setTimeout(() => setReplay('result'), 4600);
    return () => clearTimeout(t);
  }, [game?.id, live]);
  /* Bekleyen oyunda dönüş YOK: rakip henüz yok, gösterilecek sonuç da yok. */
  const phase = waiting ? 'idle' : live ? (live.phase || 'starting') : replay;

  /* ⚠️ Sayaç durumu BURADA TUTULMUYOR. Tutulsaydı her 250 ms'de tüm modal
     (iki profil, iki eşya ızgarası, madeni para) yeniden çizilirdi ve
     ekran titrerdi. RowTimer kendi içinde sayıyor; modal hiç dokunulmuyor.
     Kaynak lobi satırıyla AYNI: live.startedAt. */
  const startedAt = live?.startedAt || 0;
  const cdTotal = live?.countdown || 10;

  /* ⚠️ ÜÇ SÜTUN DAR EKRANA SIĞMALI. Sabit boyutlarla 360px'te 486px yer
     gerekiyordu — para ile profiller üst üste binerdi. Boyutlar ekrana
     göre ölçekleniyor; oran korunduğu için düzen her yerde aynı duruyor. */
  /* ⚠️⚠️ AYNI DEĞERLER İÇİN AYNI NESNE.
     setDims her çağrıda YENİ bir nesne üretiyordu. `dims` memo'lu
     HeadCol'a prop gidiyor; nesne kimliği değişince memo başarısız oluyor
     ve sütun yeniden çiziliyor. Üstelik mobilde adres çubuğu kaydırmada
     gizlenip göründüğü için `resize` sürekli tetikleniyor — yani boyut
     hiç değişmediği hâlde ekran kaydırdıkça avatarlar yeniden çiziliyordu.
     Sabit nesneleri bir kez oluşturup TEKRAR KULLANIYORUZ: boyut sınıfı
     değişmedikçe referans da değişmiyor. */
  const [dims, setDims] = useState(MM2_DIMS.lg);
  useEffect(() => {
    const measure = () => {
      const w = window.innerWidth;
      const next = w < 400 ? MM2_DIMS.sm : w < 640 ? MM2_DIMS.md : MM2_DIMS.lg;
      setDims((prev) => (prev === next ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  if (!game) return null;

  const cKey = sideOf(game);
  const cSide = SIDES[cKey];
  const jSide = SIDES[otherSide(cKey)];
  const done = phase === 'result';
  const jChance = Math.max(0, +(100 - (game.creator_chance || 0)).toFixed(1));

  /* ⚠️ "Wins" ETİKETİ YOK. Para kazananın yüzüne oturuyor ve o tarafın
     paneli aydınlanıyor — sonucu iki kez söylemek gereksiz gürültü.
     Kaybeden taraf da soluklaşmıyor: bilgi okunabilir kalmalı. */
  /* Profil sütunu — paranın yanında, büyük. */
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-5"
      /* ⚠️⚠️ backdrop-filter KALDIRILDI — ASIL KASMA KAYNAĞI BUYDU.
         Tam ekran bir bulanıklık katmanının ÜSTÜNDE bir şey animasyon
         yapınca tarayıcı her karede TÜM GÖRÜNTÜ ALANINI yeniden
         bulanıklaştırmak zorunda kalıyor. Madeni para tam da bunun
         üstünde dönüyordu: dönüş ne kadar optimize edilirse edilsin
         kareler bu yüzden düşüyordu.
         Yerine düz, biraz daha koyu bir örtü: aynı odak etkisi, sıfır
         kare başına maliyet. */
      style={{ background: 'rgba(6,7,9,.93)' }}
    >
      <motion.div
        initial={{ scale: 0.96, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.97, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[92vh] overflow-y-auto mm2-scroll rounded-2xl border border-[#2A2D31] bg-[#121417] shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3.5 border-b border-[#2A2D31] bg-[#121417]">
          <div className="flex items-baseline gap-3 min-w-0">
            <p className="font-heading font-black text-sm text-white uppercase tracking-wider">
              Coinflip
            </p>
            <span className="text-[11px] text-white/30 truncate">
              {game.at ? new Date(game.at).toLocaleString() : ''}
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 shrink-0 rounded-lg border border-[#2A2D31] bg-[#1A1D20] text-white/50 hover:text-white hover:border-[#3c4046] transition-colors flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ⚠️ ÜÇ SÜTUN: profil · para · profil.
            Para üstte, taraflar altta olduğunda gözün "kim kazandı"yı
            okuması için iki kez yukarı-aşağı gitmesi gerekiyordu. Yan yana
            dizilince para ile iki taraf tek bakışta görülüyor. */}
        <div className="px-5 pt-6 pb-4 sm:px-7">
          <div className="flex items-center justify-between gap-2 sm:gap-4 max-w-2xl mx-auto">
            <HeadCol side={cSide} name={game.creator} won={game.creator_won} done={done}
              dims={dims} canJoin={canJoin} onJoin={onJoin} />

            <div className="shrink-0 flex flex-col items-center">
              <MM2Coin
                size={dims.coin}
                phase={phase}
                creatorWins={game.creator_won}
                creatorSideKey={cKey}
                reduced={false}
              />
              {/* ⚠️ SABİT YÜKSEKLİK, İÇERİĞE YETECEK KADAR.
                  h-7 (28px) idi ama iki satırlık geri sayım ~44px yer
                  kaplıyor: yazı madeni paranın ÜSTÜNE taşıyordu. Yükseklik
                  sabit tutulmalı ki faz değişiminde madeni para zıplamasın,
                  ama en yüksek içeriği barındırmalı. mt ile de paradan
                  net bir boşluk bırakıldı. */}
              <div className="h-12 mt-1 flex items-start justify-center">
                <AnimatePresence mode="wait">
                  {phase === 'starting' && startedAt ? (
                    <motion.div key="cd" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      className="flex flex-col items-center gap-0.5">
                      <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/35">
                        flipping in
                      </span>
                      <RowTimer startingAt={startedAt} total={cdTotal}
                        className="font-heading font-black text-2xl text-white tabular-nums leading-none" />
                    </motion.div>
                  ) : done ? (
                    <motion.p key="pot" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      className="text-[11px] text-white/40">
                      Pot{' '}
                      <span className="font-heading font-black text-white ml-0.5">
                        {fmtVal(game.pot)}<span className="text-white/35 ml-0.5">$</span>
                      </span>
                    </motion.p>
                  ) : null}
                </AnimatePresence>
              </div>
            </div>

            <HeadCol side={jSide} name={game.joiner} won={!game.creator_won}
              done={done} right empty={!!waiting}
              dims={dims} canJoin={canJoin} onJoin={onJoin} />
          </div>
        </div>

        <div className="px-5 pb-5 sm:px-7 sm:pb-6">
          <div className="flex items-start gap-4 sm:gap-6">
            <StatCol side={cSide} value={game.creator_value} chance={game.creator_chance}
              items={game.creator_items} won={game.creator_won} done={done} />
            <div className="w-px self-stretch bg-[#2A2D31] shrink-0" />
            <StatCol side={jSide} value={game.joiner_value} chance={jChance}
              items={game.joiner_items} won={!game.creator_won} done={done} right />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ============================================================================
   GEÇMİŞ — son biten coinflip'ler, yatay sonuç satırları

   ⚠️ Lobideki GameRow ile AYNI yatay ritim, ama katılım eylemi yok: bunlar
      bitmiş oyunlar. "View" satırı yerinde genişletiyor — ayrı bir sayfaya
      götürmek, kullanıcıyı lobiden koparırdı.
============================================================================ */
const FinishedRow = ({ g, onOpen }) => {
  /* ⚠️ İki tarafın eşyaları BİRLEŞTİRİLİR: satırda gösterilen şey "bu
     oyunda ne oynandı" — kimin koyduğu ayrımı View ekranında yapılıyor.
     Eski kayıtlarda alan yok (null) → boş dizi, şerit "No items" der. */
  const potItems = useMemo(
    () => [...(g.creator_items || []), ...(g.joiner_items || [])],
    [g.creator_items, g.joiner_items]
  );
  const cKey = sideOf(g);
  const winnerKey = g.creator_won ? cKey : otherSide(cKey);
  const side = SIDES[winnerKey];

  return (
    <motion.div
      layout
      className="relative rounded-xl border border-[#2A2D31] bg-[#121417] overflow-hidden hover:border-[#3c4046] transition-colors"
    >
      <div className="absolute inset-y-0 left-0 w-[3px]" style={{ background: side.base }} />
      <div className="flex items-center gap-3 pl-4 pr-3 py-2.5">
        {/* kazanan */}
        <div className="flex items-center gap-2 min-w-0 w-[150px] sm:w-[180px] shrink-0">
          <div className="shrink-0"><CoinFace sideKey={winnerKey} size={26} /></div>
          <div className="min-w-0">
            <p className="font-bold text-white text-[12px] truncate">{g.winner}</p>
            <p className="text-[10px] text-[#2ecf6e] leading-tight">won</p>
          </div>
        </div>

        {/* ⚠️ ORTA GRUP: SABİT GENİŞLİKLİ ve ORTALANMIŞ.
            Önceden `flex-1` idi ve tüm boşluğu yutuyordu: sohbet kapanıp
            satır genişleyince metin sola yapışıyor, sağda kocaman bir
            boşluk kalıyordu. Şimdi esneyen kap ortalıyor, içerideki grup
            ise `max-w` ile sınırlı — referans tasarımdaki gibi satırın
            ortasında derli toplu duruyor.
            ⚠️ Eşyalar da BURADA: geçmiş satırlarında hiç gösterilmiyordu,
            oysa "hangi eşyalar oynandı" en çok merak edilen şey. */}
        <div className="flex-1 min-w-0 flex items-center justify-center">
          <div className="w-full max-w-[520px] flex items-center gap-3 min-w-0">
            <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-white/45 shrink-0 w-[150px] justify-end">
              <span className={`truncate ${g.creator_won ? 'text-white/80 font-bold' : ''}`}>
                {g.creator}
              </span>
              <span className="text-white/20 shrink-0">vs</span>
              <span className={`truncate ${!g.creator_won ? 'text-white/80 font-bold' : ''}`}>
                {g.joiner}{g.vs_bot ? ' (bot)' : ''}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <ItemStrip items={potItems} align="center" size={34} />
            </div>
          </div>
        </div>

        {/* pot */}
        <div className="shrink-0 text-right">
          <p className="font-heading font-black text-[13px] text-[#2ecf6e] tabular-nums leading-none">
            {fmtVal(g.pot)}
          </p>
          <p className="text-[9px] text-white/25 mt-0.5">pot</p>
        </div>

        {/* ⚠️ Satır İÇİNDE açmak yerine MODAL: satır içi genişleme, altındaki
            tüm satırları aşağı itip kullanıcının baktığı yeri kaydırıyordu. */}
        <button
          onClick={() => onOpen(g)}
          className="px-3 py-1.5 rounded-lg bg-[#1A1D20] border border-[#2A2D31] text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-white hover:border-[#3c4046] transition-colors flex items-center gap-1.5 shrink-0"
        >
          <Eye className="w-3 h-3" />
          <span className="hidden sm:inline">View</span>
        </button>
      </div>
    </motion.div>
  );
};

const FinishedList = ({ games = [] }) => {
  const [shown, setShown] = useState(null);
  if (!games.length) return null;
  return (
    <div className="mt-6">
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/30 mb-2">
        Previous flips
      </p>
      <div className="flex flex-col gap-1.5">
        {games.slice(0, 10).map((g) => (
          <FinishedRow key={g.id} g={g} onOpen={setShown} />
        ))}
      </div>
      <AnimatePresence>
        {shown && <ResultModal game={shown} onClose={() => setShown(null)} />}
      </AnimatePresence>
    </div>
  );
};

/* ============================================================================
   YATAY OYUN SATIRI

   ⚠️ Izgara yerine SATIR: bir coinflip'in taşıdığı bilgi (kim, ne kadar,
      hangi eşyalar, ne yapabilirim) doğal olarak yataydır. Izgarada her kart
      ~260px yükseklik kaplıyor ve ekrana 6 oyun zor sığıyordu; satırda aynı
      alana 3 katı sığıyor ve göz tek dikey eksende tarıyor.
============================================================================ */
/* ⚠️⚠️ SAYAÇ AYRI BİLEŞEN — SATIRIN TAMAMI YENİDEN ÇİZİLMESİN.
   Sayaç durumu GameRow içindeyken her 250 ms'de TÜM satır yeniden
   çiziliyordu: avatar, eşya şeridi, madeni para, butonlar. Alt bileşenler
   memo'lu olsa bile ağaç baştan yürünüyor ve ekranda gözle görülür bir
   titreme oluyordu. Sayaç kendi bileşeninde olunca yalnızca o rakam
   güncelleniyor; satırın geri kalanı hiç dokunulmadan kalıyor.
   ⚠️ Kalan süre Date.now() FARKINDAN hesaplanır, azaltılarak değil: sekme
   arka plana alınıp geri gelse bile doğru kalır. */
const RowTimer = React.memo(({ startingAt, total, className, children }) => {
  const [left, setLeft] = useState(() =>
    startingAt ? Math.max(0, total - Math.floor((Date.now() - startingAt) / 1000)) : 0
  );
  useEffect(() => {
    if (!startingAt) { setLeft(0); return; }
    const tick = () =>
      setLeft(Math.max(0, total - Math.floor((Date.now() - startingAt) / 1000)));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [startingAt, total]);
  return <span className={className}>{children ? children(left) : left}</span>;
});

const GameRow = React.memo(({ game, meId, onJoin, onCancel, onBot, onView, config, busyId }) => {
  const startingAt = game._startingAt || 0;
  const total = game._countdown || 10;

  const counting = startingAt > 0;
  const cSideKey = sideOf(game);
  const cSide = SIDES[cSideKey];
  const jSide = SIDES[otherSide(cSideKey)];
  const mine = meId && game.creator_id === meId;
  const busy = busyId === game.id;

  /* ⚠️ Bot çağırma KASA HAVUZUNA bağlı. Havuz boşken buton gösterilirse
     kullanıcı basıp anlamadığı bir hata alır; bunun yerine nedenini
     gösteren pasif bir rozet çiziyoruz. */
  const pool = config?.house_pool;
  /* ⚠️ SADECE can_call_bot'a BAKMAK YETMEZ. O bayrak "havuzda eşya var mı"
     der; sunucu ise bu OYUNUN değerine denk gelen bir küme arar. Havuzda
     10 değerinde tek eşya varken 10.000'lik bir oyuna bot çağrılamaz ama
     buton açık görünüyordu: kullanıcı basıp 409 alıyordu. Havuz toplamı
     oyunun altına düşüyorsa butonu baştan kapatıyoruz. */
  const poolValue = Number(pool?.value) || 0;
  const poolCovers = poolValue >= (game.creator_value || 0) * 0.95;
  const botAllowed =
    config?.bot_enabled &&
    game.creator_value <= (config?.bot_max_value ?? 0) &&
    !!pool?.can_call_bot &&
    poolCovers;
  /* ⚠️ NEDEN SOMUT OLMALI. "Bot unavailable" gibi bir metin kullanıcıya
     bekleyip beklememesi gerektiğini söylemiyor. Havuzun ne kadar olduğunu
     ve ne kadar gerektiğini yazınca karar verebiliyor. */
  const botReason = !config?.bot_enabled
    ? 'Bot games are disabled'
    : game.creator_value > (config?.bot_max_value ?? 0)
    ? `Bot games are limited to ${fmtVal(config?.bot_max_value ?? 0)}`
    : !pool?.can_call_bot
    ? 'House pool is empty — it fills from coinflip fees'
    : !poolCovers
    ? `House pool has ${fmtVal(poolValue)} — this game needs ${fmtVal(game.creator_value)}`
    : '';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 340, damping: 30 }}
      className="relative rounded-xl border border-[#2A2D31] bg-[#15171a] overflow-hidden hover:border-[#3c4046] transition-colors"
    >
      <div className="absolute inset-y-0 left-0 w-[3px]" style={{ background: cSide.base }} />

      <div className="relative flex items-center gap-3 sm:gap-4 pl-4 pr-3 py-3">
        {/* kurucu */}
        <div className="flex items-center gap-2.5 min-w-0 w-[126px] sm:w-[172px] shrink-0">
          {/* ⚠️ SİTE adı geçilmeli. Uç /api/user/avatar/{username} sorgusunu
              `site_username` üzerinden yapıyor; Roblox adı önce geçilince
              ikisi farklı olan kullanıcılarda arama 404 dönüyor ve fotoğraf
              yerine baş harfler görünüyordu. */}
          <Avatar
            username={game.creator_username}
            size={40}
            ring={cSide.base}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="font-bold text-white text-[13px] truncate">{game.creator_username}</p>
              {mine && (
                <span className="text-[9px] font-black uppercase px-1 py-0.5 rounded bg-[#B059E8]/20 text-[#c98bf0] shrink-0">
                  you
                </span>
              )}
            </div>
            <p className="font-heading font-black text-[15px] text-[#2ecf6e] tabular-nums leading-tight">
              {fmtVal(game.creator_value)}
            </p>
          </div>
        </div>

        {/* taraf madeni */}
        <div className="shrink-0 hidden sm:block">
          <CoinFace sideKey={cSideKey} size={30} />
        </div>

        {/* ⚠️ ORTA ALAN: esner ve İÇERİĞİ ORTALAR. Sohbet kapanınca satır
            genişliyor; sola yaslı içerik o boşlukta yalnız kalıyordu.
            ItemStrip artık kap genişliğini ölçüp sığan kadar eşya
            gösterdiği için genişleyen alan otomatik doluyor. */}
        {/* ⚠️ ORTA GRUP TEK KAPTA VE ORTALANMIŞ.
            Eşya şeridi ile katılım aralığı ayrı ayrı `flex-1`/`ml-auto`
            kullanıyordu; bu ikisi birbiriyle çakışıyor ve satır genişleyince
            içerik sola yığılıyordu. Tek bir esneyen kap, içindekileri
            `justify-center` ile ortalıyor — sohbet açık ya da kapalı olsun
            grup satırın ortasında kalıyor. */}
        {/* ⚠️ Eşya alanı SINIRLI. Sınırsız `flex-1` ile eşyalar satırın
            tamamına yayılıyordu; referans tasarımda derli toplu bir grup
            halinde ortada duruyor. max-w ile sınırlayıp ortalıyoruz —
            sohbet kapanınca grup ortada kalıyor, kenara savrulmuyor. */}
        <div className="flex-1 min-w-0 flex items-center justify-center gap-3 sm:gap-4">
          <div className="min-w-0 flex-1 max-w-[460px] overflow-hidden">
            {/* ⚠️ ALAN ADI: sunucu `creator_items` gönderiyor.
                `creator_items_public` iç isimdir ve yayına ÇIKMAZ —
                onu okumak her satırda "No items" yazdırıyordu. */}
            <ItemStrip items={game.creator_items} align="center" />
          </div>

        {/* katılım aralığı */}
        <div className="hidden lg:flex flex-col items-center shrink-0 w-[124px]">
          <span className="text-[9px] font-bold uppercase tracking-wider text-white/30">
            {jSide.label} side
          </span>
          <span className="text-[11px] font-bold text-white/60 tabular-nums">
            {fmtVal(game.min_join_value)} – {fmtVal(game.max_join_value)}
          </span>
        </div>

        {/* ⚠️ EYLEM ALANI SABİT: eşya şeridi esnek olduğu için butonları
            sıkıştırıp "Cancel"i kırpıyordu. Sabit genişlikle butonlar her
            zaman tam sığıyor, daralan taraf eşya şeridi oluyor. */}
        </div>

        {/* ⚠️ SABİT GENİŞLİK KALDIRILDI. 196px, "Bot" + "Cancel" birlikte
            göründüğünde yetmiyor ve Cancel taşıyordu. İçeriğe göre genişleyen
            ama ASLA sıkışmayan bir alan doğru çözüm: shrink-0 + nowrap ile
            butonlar tam boyunda kalıyor, daralan taraf eşya şeridi oluyor.
            ⚠️ ml-auto KALDIRILDI: orta grup zaten flex-1 ile boşluğu
            yutuyor; ikisi birlikte hizalamayı bozuyordu. */}
        <div className="flex items-center justify-end gap-1.5 shrink-0">
          {counting ? (
            /* ⚠️ Katılım kapandı: Join yerine GRİ "View". Rengi değiştirmek
               tek başına yetmez, işlevi de değişmeli — aksi halde kullanıcı
               dolu bir oyuna katılmayı deneyip hata alırdı. */
            <>
              {/* ⚠️ "Joining" YOK: katılan kişi doğrudan giriyor, kimse
                  kimseyi bloklamıyor. Satır sadece ne zaman döneceğini
                  söylüyor. */}
              <span className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#0E0F11] border border-[#2A2D31] tabular-nums">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/35">
                  flipping in
                </span>
                <RowTimer startingAt={startingAt} total={total}
                  className="font-heading font-black text-sm text-white" />
              </span>
              <button
                onClick={() => onView?.(game.id)}
                className="px-4 sm:px-6 py-2.5 rounded-lg bg-[#2A2D31] border border-[#3c4046] font-heading font-black text-[11px] uppercase tracking-wider text-white/70 hover:text-white hover:bg-[#3a3d43] transition-colors flex items-center gap-1.5"
              >
                <Eye className="w-3.5 h-3.5" />
                View
                <RowTimer startingAt={startingAt} total={total}
                  className="sm:hidden tabular-nums" />
              </button>
            </>
          ) : mine ? (
            <>
              {botAllowed ? (
                <button
                  onClick={() => onBot(game.id)}
                  disabled={busy}
                  className="px-2.5 py-2 rounded-lg bg-[#1A1D20] border border-[#2A2D31] text-[11px] font-bold uppercase tracking-wider text-white/70 hover:text-white hover:border-[#B059E8]/50 transition-colors disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap shrink-0"
                >
                  <Bot className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Bot</span>
                </button>
              ) : botReason ? (
                <span
                  title={botReason}
                  className="hidden md:flex px-3 py-2 rounded-lg bg-[#1A1D20] border border-dashed border-[#2A2D31] text-[10px] font-bold uppercase tracking-wider text-white/25 items-center gap-1.5 cursor-help"
                >
                  <Bot className="w-3.5 h-3.5" /> n/a
                </span>
              ) : null}
              <button
                onClick={() => onCancel(game.id)}
                disabled={busy}
                className="px-2.5 py-2 rounded-lg bg-[#1A1D20] border border-[#2A2D31] text-[11px] font-bold uppercase tracking-wider text-white/60 hover:text-white hover:border-[#3c4046] transition-colors disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap shrink-0"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">Cancel</span>
              </button>
            </>
          ) : (
            <>
              {/* ⚠️ AÇIK OYUNLAR DA İZLENEBİLİR. Katılmadan önce karşı
                  tarafın ne koyduğunu büyük görmek isteyen için View;
                  katılma da o ekrandan yapılabiliyor. */}
              <button
                onClick={() => onView?.(game.id)}
                className="px-3 py-2.5 rounded-lg bg-[#1A1D20] border border-[#2A2D31] text-white/55 hover:text-white hover:border-[#3c4046] transition-colors flex items-center shrink-0"
                title="View"
              >
                <Eye className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onJoin(game)}
                disabled={busy}
                className="px-4 sm:px-6 py-2.5 rounded-lg font-heading font-black text-[11px] uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0 flex items-center gap-1.5 shrink-0"
                style={{ background: jSide.base, boxShadow: `0 8px 20px -10px ${jSide.base}` }}
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Swords className="w-3.5 h-3.5" />}
                Join
              </button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
});

/* ============================================================================
   CANLI FLIP KARTI — lobide dönen oyunlar
============================================================================ */
const LiveFlipCard = ({ live, onOpen }) => {
  const g = live.game;
  const cKey = sideOf(g);
  const winnerSide = live.creator_wins == null
    ? null
    : (live.creator_wins ? cKey : otherSide(cKey));

  return (
    <motion.button
      layout
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.94 }}
      onClick={onOpen}
      className="relative shrink-0 w-[190px] rounded-xl border bg-[#15171a] p-3 text-left overflow-hidden transition-colors hover:border-[#3c4046]"
      style={{ borderColor: winnerSide ? `${SIDES[winnerSide].base}66` : '#2A2D31' }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] font-black uppercase tracking-widest text-white/35">
          {live.phase === 'result' ? 'done' : live.phase === 'flipping' ? 'flipping' : 'starting'}
        </span>
        {live.phase !== 'result' && (
          <span className="w-1.5 h-1.5 rounded-full bg-[#E5484D] animate-pulse" />
        )}
      </div>

      <div className="flex items-center justify-center gap-2 mb-2">
        <Avatar username={g.creator_roblox_username || g.creator_username} size={28} ring={SIDES[cKey].base} />
        <MM2Coin
          size={44}
          phase={live.phase}
          creatorWins={live.creator_wins ?? null}
          creatorSideKey={cKey}
          reduced
        />
        <Avatar
          username={g.joiner_roblox_username || g.joiner_username}
          isBot={g.joiner_is_bot}
          size={28}
          ring={SIDES[otherSide(cKey)].base}
        />
      </div>

      <p className="text-center font-heading font-black text-sm text-white">
        {fmtVal((g.creator_value || 0) + (g.joiner_value || 0))}
      </p>
      {live.phase === 'result' && live.winner_username && (
        <p className="text-center text-[10px] font-bold truncate mt-0.5"
          style={{ color: winnerSide ? SIDES[winnerSide].text : '#fff' }}>
          {live.winner_username}
        </p>
      )}
    </motion.button>
  );
};

/* ============================================================================
   TRADE SATIRI — bekleyen/biten deposit & withdraw
============================================================================ */

const TERMINAL = ['done', 'failed', 'cancelled'];

/* ----------------------------------------------------------------------------
   ROBLOX ADI ALANI
   ----------------------------------------------------------------------------
   Profildeki ad DOLU GELİR ama değiştirilebilir. Değiştirilen ad YALNIZCA o
   işlem için geçerlidir; sitedeki kayıtlı ad DEĞİŞMEZ.

   ⚠️ Bu ayrım kullanıcıya AÇIKÇA söylenmeli. Aksi halde "burada değiştirirsem
      profilim de değişir mi" tereddüdü yaşar ve alternatif hesabına işlem
      yapmaktan çekinir.
---------------------------------------------------------------------------- */
const RBX_RE = /^[A-Za-z0-9_]{3,20}$/;

const RobloxField = ({ value, onChange, profileName, disabled }) => {
  const v = value.trim();
  const changed = v && v.toLowerCase() !== (profileName || '').toLowerCase();
  const invalid = v && !RBX_RE.test(v);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[11px] font-bold uppercase tracking-wider text-white/45">
          Roblox account
        </label>
        {changed && !invalid && (
          <span className="text-[10px] font-bold text-[#F5A524]">this trade only</span>
        )}
      </div>
      <div className="relative">
        <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25" />
        <input
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.replace(/[^A-Za-z0-9_]/g, '').slice(0, 20))}
          placeholder={profileName || 'Your Roblox username'}
          spellCheck={false}
          autoComplete="off"
          className={`w-full pl-9 pr-3 py-2.5 rounded-lg bg-[#15171a] border text-sm text-white placeholder-white/25 focus:outline-none transition-colors disabled:opacity-50 ${
            invalid ? 'border-[#E5484D]/60' : 'border-[#2A2D31] focus:border-[#B059E8]/60'
          }`}
        />
      </div>
      <p className="text-[10px] text-white/30 mt-1.5 leading-relaxed">
        {invalid
          ? '3-20 characters — letters, numbers and underscore only.'
          : changed
            ? 'This trade only'
            : 'Send the trade from this account'}
      </p>
    </div>
  );
};

/* ----------------------------------------------------------------------------
   YATIRMA ONAYI
---------------------------------------------------------------------------- */
const DepositModal = ({ open, profileName, busy, onClose, onStart }) => {
  const [name, setName] = useState('');
  useEffect(() => { if (open) setName(profileName || ''); }, [open, profileName]);
  if (!open) return null;
  const valid = RBX_RE.test(name.trim());

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
      /* ⚠️ blur 8px → 4px: bulanıklık yarıya inince tarayıcının her
         karede işlediği piksel alanı da yarıya iniyor. Görsel fark
         neredeyse yok, açılış belirgin şekilde akıcılaşıyor. */
      style={{ background: 'rgba(6,7,9,.86)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 30, scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-t-2xl sm:rounded-2xl border border-[#2A2D31] bg-[#0E0F11] overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-[#2A2D31] flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <ArrowDownToLine className="w-4 h-4 text-[#2ecf6e]" />
            <h3 className="font-heading font-black text-white text-sm">Deposit items</h3>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 rounded-lg bg-[#15171a] border border-[#2A2D31] flex items-center justify-center text-white/50 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <p className="text-xs text-white/45 leading-relaxed">
            We'll pair you with a free bot and give you its server link. Join,
            send the bot a trade request with your items, and they land in your
            inventory.
          </p>
          <RobloxField value={name} onChange={setName}
            profileName={profileName} disabled={busy} />
        </div>

        <div className="px-5 py-4 border-t border-[#2A2D31] bg-[#0b0c0e]">
          <button
            onClick={() => onStart(name.trim())}
            disabled={!valid || busy}
            className={`w-full px-4 py-3 rounded-xl font-heading font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${
              !valid || busy
                ? 'bg-[#1A1D20] text-white/30 cursor-not-allowed border border-[#2A2D31]'
                : 'bg-[#16A34A] text-white hover:bg-[#15913f]'
            }`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownToLine className="w-4 h-4" />}
            {busy ? 'Finding a bot' : 'Start deposit'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ----------------------------------------------------------------------------
   GERİ SAYIM
   ----------------------------------------------------------------------------
   Botun sabrı sınırlı: bu süre içinde bota takas isteği GÖNDERİLMEZSE pes
   eder ve işlem başarısız olur. Takası bot AÇMAZ — kullanıcı gönderir.
   Kullanıcının bunu GÖRMESİ gerekiyor — "bir ara giderim" ile "90 saniyem
   var" arasında davranış farkı var.

   ⚠️ Son tarih SUNUCUDAN gelir, istemcide hesaplanmaz. İstemci saati yanlış
      ayarlıysa kullanıcı yanlış süre görürdü. Kalan süre, sunucunun verdiği
      ISO zaman damgasıyla tarayıcının şu anı arasındaki farktır; ilk
      okumada aradaki sapma da hesaba katılır.
---------------------------------------------------------------------------- */
const Countdown = ({ deadline, size = 64, label }) => {
  const [left, setLeft] = useState(null);
  const totalRef = useRef(null);

  useEffect(() => {
    if (!deadline) { setLeft(null); return; }
    const end = new Date(deadline).getTime();
    if (!Number.isFinite(end)) { setLeft(null); return; }
    const tick = () => {
      const s = Math.max(0, Math.round((end - Date.now()) / 1000));
      // Toplam süreyi İLK ölçümde sabitle; her turda yeniden hesaplasaydık
      // halka hiç ilerlemezdi (kalan/toplam oranı sabit kalırdı).
      if (totalRef.current === null) totalRef.current = Math.max(s, 1);
      setLeft(s);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => { clearInterval(id); totalRef.current = null; };
  }, [deadline]);

  if (left === null) return null;

  const total = totalRef.current || 1;
  const pct = Math.max(0, Math.min(1, left / total));
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const urgent = left <= 20;
  const color = urgent ? '#E5484D' : left <= 45 ? '#F5A524' : '#B059E8';

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="#2A2D31" strokeWidth="3" />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth="3" strokeLinecap="round"
          strokeDasharray={circ}
          animate={{ strokeDashoffset: circ * (1 - pct) }}
          transition={{ duration: 0.9, ease: 'linear' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          key={urgent}
          animate={urgent ? { scale: [1, 1.08, 1] } : { scale: 1 }}
          transition={{ duration: 1, repeat: urgent ? Infinity : 0 }}
          className="font-heading font-black tabular-nums leading-none"
          style={{ color, fontSize: size * 0.28 }}
        >
          {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}
        </motion.span>
        {label && (
          <span className="text-[7px] font-bold uppercase tracking-wider text-white/30 mt-0.5">
            {label}
          </span>
        )}
      </div>
    </div>
  );
};

const STEP_TEXT = {
  queued:   { t: 'Getting the bot ready',  s: 'Join the server below, then send the bot a trade request.' },
  sent:     { t: 'Bot is waiting for you', s: 'Join the server and send the bot a trade request — it accepts automatically.' },
  settling: { t: 'Finishing up',           s: 'Confirming the trade — this takes a moment.' },
  done:     { t: 'Completed',              s: '' },
  failed:   { t: 'Failed',                 s: '' },
  cancelled:{ t: 'Cancelled',              s: '' },
  waiting_leg: { t: 'Up next', s: 'Unlocks when the previous step finishes.' },
  needs_review:{ t: 'Under review', s: 'Our team is checking this — your items are safe.' },
};

const TradeSession = ({ session, onClose, onCancel, busyId }) => {
  const steps = session?.steps || [];
  const active = session?.active_step;
  const multi = (session?.total_steps || 1) > 1;
  const isDeposit = session?.type === 'deposit';
  /* ⚠️ "Deposit in progress" YETMİYOR. Aynı cümle iki bambaşka işlem için
     kullanılınca oyuncu, cüzdandan başlattığı MM Coin yatırmasını burada
     görüp "coinflip eşyalarım mı geliyor?" diye düşünüyordu. Cüzdan
     yatırması artık ADIYLA anılıyor ve nereye gideceği yazıyor. */
  const isWalletDep = isWalletDepositSession(session);
  const txt = STEP_TEXT[active?.status] || STEP_TEXT.queued;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(6,7,9,.86)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, scale: 0.97 }} animate={{ y: 0, scale: 1 }} exit={{ y: 30, scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-2xl sm:rounded-2xl border border-[#2A2D31] bg-[#0E0F11] overflow-hidden"
        style={{ boxShadow: '0 40px 90px -30px rgba(0,0,0,.95)' }}
      >
        {/* başlık */}
        <div className="px-5 py-4 border-b border-[#2A2D31] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#1A1D20] border border-[#2A2D31] flex items-center justify-center shrink-0">
            {isDeposit ? <ArrowDownToLine className="w-4 h-4 text-[#2ecf6e]" />
                       : <ArrowUpFromLine className="w-4 h-4 text-[#B059E8]" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading font-black text-white text-sm">
              {!isDeposit ? 'Withdrawal in progress'
                : isWalletDep ? 'Wallet deposit in progress'
                : 'Item deposit in progress'}
            </p>
            {isDeposit && (
              <p className="text-[11px] text-white/40">
                {isWalletDep
                  ? 'These items become MM Coin — they will not enter your coinflip inventory.'
                  : 'These items go into your coinflip inventory.'}
              </p>
            )}
            {multi && (
              <p className="text-[11px] text-white/40">
                Step {(active?.leg_index ?? 0) + 1} of {session.total_steps} ·
                {' '}your items are spread across {session.total_steps} bots
              </p>
            )}
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 rounded-lg bg-[#15171a] border border-[#2A2D31] flex items-center justify-center text-white/50 hover:text-white transition-colors shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* çok adımlıysa ilerleme */}
        {multi && (
          <div className="px-5 pt-4 flex items-center gap-2">
            {steps.map((st, i) => {
              const ok = st.status === 'done';
              const bad = ['failed', 'cancelled'].includes(st.status);
              const now = st.trade_id === active?.trade_id;
              return (
                <React.Fragment key={st.trade_id}>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <motion.div
                      animate={{ scale: now ? [1, 1.12, 1] : 1 }}
                      transition={{ duration: 1.6, repeat: now ? Infinity : 0 }}
                      className="w-6 h-6 rounded-full flex items-center justify-center font-heading font-black text-[10px]"
                      style={{
                        background: ok ? '#16A34A' : bad ? '#E5484D' : now ? '#B059E8' : '#1A1D20',
                        color: ok || bad || now ? '#fff' : 'rgba(255,255,255,.35)',
                        border: !ok && !bad && !now ? '1px solid #2A2D31' : 'none',
                      }}
                    >
                      {ok ? <Check className="w-3.5 h-3.5" strokeWidth={3.5} /> : i + 1}
                    </motion.div>
                  </div>
                  {i < steps.length - 1 && (
                    <div className="flex-1 h-px bg-[#2A2D31] relative overflow-hidden">
                      <motion.div className="absolute inset-y-0 left-0 bg-[#16A34A]"
                        initial={{ width: 0 }} animate={{ width: ok ? '100%' : '0%' }}
                        transition={{ duration: 0.4 }} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* aktif adım */}
        <div className="px-5 py-5">
          {active ? (
            <>
              <div className="flex items-start gap-4 mb-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Loader2 className="w-3.5 h-3.5 text-[#B059E8] animate-spin" />
                    <p className="font-heading font-black text-white text-base">{txt.t}</p>
                  </div>
                  <p className="text-xs text-white/45 leading-relaxed">{txt.s}</p>
                  {active.roblox_username && (
                    <p className="text-[11px] text-white/35 mt-2">
                      Trading as{' '}
                      <span className="text-white/70 font-semibold">
                        {active.roblox_username}
                      </span>
                    </p>
                  )}
                </div>
                {/* Botun sabri bitmeden yetismesi gereken sure */}
                {active.join_deadline && active.status === 'sent' && (
                  <Countdown deadline={active.join_deadline} size={62} label="left" />
                )}
              </div>

              {/* bot kartı + katılma butonu */}
              <div className="rounded-xl border border-[#2A2D31] bg-[#15171a] p-4">
                <div className="flex items-center gap-2.5 mb-3">
                  <Bot className="w-4 h-4 text-[#c98bf0]" />
                  <span className="text-sm font-bold text-white">{active.bot_name}</span>
                  {active.bot_roblox && (
                    <span className="text-[11px] text-white/35 truncate">@{active.bot_roblox}</span>
                  )}
                  <span className={`ml-auto inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${
                    active.bot_online ? 'text-[#2ecf6e] bg-[#16A34A]/15' : 'text-[#F5A524] bg-[#F5A524]/15'}`}>
                    <span className={`w-1 h-1 rounded-full ${active.bot_online ? 'bg-[#2ecf6e] animate-pulse' : 'bg-[#F5A524]'}`} />
                    {active.bot_online ? 'online' : 'offline'}
                  </span>
                </div>

                {active.items?.length > 0 && (
                  <p className="text-[11px] text-white/40 mb-3 leading-relaxed">
                    {active.items.join(', ')}
                  </p>
                )}

                {active.join_link ? (
                  <a href={active.join_link} target="_blank" rel="noopener noreferrer"
                    className="w-full px-4 py-3.5 rounded-xl bg-[#B059E8] text-white font-heading font-black text-sm uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#c176f0] transition-all hover:-translate-y-0.5 shadow-[0_12px_30px_-12px_rgba(176,89,232,.9)]">
                    <Server className="w-4 h-4" />
                    Join server
                    <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                  </a>
                ) : (
                  <div className="px-4 py-3 rounded-xl bg-[#F5A524]/10 border border-[#F5A524]/30 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#F5A524] shrink-0 mt-px" />
                    <p className="text-[11px] text-[#FFCB6B] leading-relaxed">
                      This bot has no private server link configured yet, so we
                      can't send you to it. Our team has been notified.
                    </p>
                  </div>
                )}
              </div>

              {active.status === 'sent' && (
                <p className="mt-3 text-[11px] text-white/35 leading-relaxed">
                  If you don't send the trade request in time the bot moves on
                  and your items stay safe in your inventory.
                </p>
              )}

              {active.status === 'queued' && (
                <button
                  onClick={() => onCancel(active.trade_id)}
                  disabled={busyId === active.trade_id}
                  className="w-full mt-3 py-2.5 rounded-xl bg-transparent text-white/40 text-[11px] font-bold uppercase tracking-wider hover:text-[#E5484D] transition-colors disabled:opacity-40"
                >
                  {busyId === active.trade_id ? 'Cancelling…' : 'Cancel'}
                </button>
              )}
            </>
          ) : session?.needs_review ? (
            /* ⚠️ "Bitti" DEĞİL. Eşyalar kilitli, insan kararı bekliyor. */
            <div className="py-5 text-center">
              <ShieldCheck className="w-9 h-9 mx-auto mb-3 text-[#F5A524]" />
              <p className="font-heading font-black text-white mb-1">Under review</p>
              <p className="text-xs text-white/45 leading-relaxed max-w-xs mx-auto">
                We couldn't confirm this trade automatically, so our team is
                checking it. Your items are held safely and nothing is lost —
                you'll get them or the payout once it's resolved.
              </p>
            </div>
          ) : (
            <div className="py-6 text-center">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-[#2ecf6e]" />
              <p className="font-heading font-black text-white">All steps completed</p>
            </div>
          )}
        </div>

        {/* tamamlanan adımlar */}
        {multi && steps.some((st) => st.status === 'done') && (
          <div className="px-5 pb-5 space-y-1.5">
            {steps.filter((st) => st.status === 'done').map((st) => (
              <div key={st.trade_id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#16A34A]/8 border border-[#16A34A]/25">
                <Check className="w-3.5 h-3.5 text-[#2ecf6e] shrink-0" strokeWidth={3} />
                <span className="text-[11px] text-white/60 truncate">
                  Step {st.leg_index + 1} · {st.bot_name}
                </span>
                <span className="ml-auto text-[10px] font-bold text-[#2ecf6e] shrink-0">done</span>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

/* ============================================================================
   ANA SAYFA
============================================================================ */
export const Mm2game = () => {
  const { user, token } = useAuth();
  const { chatOpen } = useChatLayout();

  const [tab, setTab] = useState('coinflip');       // coinflip | inventory
  const [config, setConfig] = useState(null);
  const [games, setGames] = useState([]);           // bekleyen lobiler
  const [liveFlips, setLiveFlips] = useState({});   // game_id -> canlı flip
  const [focusId, setFocusId] = useState(null);     // arenada açık oyun
  const [inventory, setInventory] = useState([]);
  const [invValue, setInvValue] = useState(0);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsUp, setWsUp] = useState(false);
  // Canlı bot durumu. Kullanıcı yatırmaya basmadan ÖNCE bilmeli: bot yoksa
  // butona basıp hata almak yerine butonun neden kapalı olduğunu görsün.
  const [botStat, setBotStat] = useState(null);   // {total, online, available}
  const [recent, setRecent] = useState([]);       // son biten coinflip'ler
  const [lastResultAt, setLastResultAt] = useState(0);  // havuz tazeleme tetikleyicisi
  const [session, setSession] = useState(null);   // açık işlem (pencereyi besler)
  const [sessionOpen, setSessionOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  // Envanter büyüdükçe (yüzlerce eşya) ızgarayı gözle taramak imkânsız hale
  // geliyordu; arama ve sıralama modalda vardı ama ASIL ızgarada yoktu.
  const [invQuery, setInvQuery] = useState('');
  const [invSort, setInvSort] = useState('value_desc');
  const [lobbySort, setLobbySort] = useState('value');

  const [modal, setModal] = useState(null);         // {mode, game} | null
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const closedRef = useRef(false);
  // ⚠️ Katıldığım oyunların id'leri. WS işleyicisi [token, meId] ile kurulduğu
  //    için içeride `liveFlips`i okumak BAYAT değer verir (efekt kurulduğu andaki
  //    boş nesne). Ref her zaman güncel → sonuç geldiğinde envanteri gerçekten
  //    ilgili kullanıcı için tazeleyebiliyoruz.
  const myGamesRef = useRef(new Set());
  // Sonuç gösterimi bitince canlı flip'i düşüren zamanlayıcılar. Sayfadan
  // çıkılırsa hepsi iptal edilir — aksi halde 9 saniye boyunca sökülmüş
  // bileşene setState çağrısı yapan zamanlayıcılar ayakta kalırdı.
  const timersRef = useRef(new Set());
  const meId = user?.id;

  useEffect(() => () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current.clear();
  }, []);

  // hareket azaltma tercihi — animasyonlar buna saygı duyar
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);

  const authHeaders = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    [token]
  );

  /* ------------------------------------------------------------ VERİ ÇEKME */
  const loadInventory = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/game/mm2/inventory`, authHeaders);
      setInventory(r.data?.items || []);
      setInvValue(r.data?.available_value || 0);
    } catch (e) {
      // 401 dışındaki hatalarda sessiz kal: envanter arka planda tazeleniyor,
      // her hatada toast atmak kullanıcıyı boğar.
      if (e?.response?.status === 401) toast.error('Your session expired — please sign in again.');
    }
  }, [token, authHeaders]);

  const loadTrades = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/game/mm2/trades`, authHeaders);
      setTrades(r.data?.trades || []);
    } catch { /* sessiz */ }
  }, [token, authHeaders]);

  const loadBotStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/game/mm2/bot-status`);
      setBotStat(r.data || null);
    } catch { /* sessiz — buton yine de denenebilir kalır */ }
  }, []);

  /* ⚠️ Hata durumunda LİSTEYE DOKUNULMAZ. Boşaltmak, anlık bir ağ
     titremesinde şeridin gözden kaybolup geri gelmesine yol açardı. */
  const loadRecent = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/game/mm2/recent`);
      if (!Array.isArray(r.data?.games)) return;
      /* ⚠️ AYNI COINFLIP İKİ KEZ — sunucu geçmişte aynı `id`yi iki kayıt
         olarak dönebiliyor (ör. sonuç yayını iki kez işlenip önbelleğe iki
         kez yazıldığında). React `key={g.id}` ile çizdiği için liste bunu
         olduğu gibi ekrana basıyordu. Burada id'ye göre tekilleştiriyoruz:
         İLK görülen kayıt tutulur, sonrası atılır (sunucu yeniden eskiye
         doğru sıralı gönderiyor, yani ilk = en güncel). */
      const seen = new Set();
      const uniq = [];
      for (const g of r.data.games) {
        const k = g?.id;
        if (k == null) { uniq.push(g); continue; }   // id'siz kayda dokunma
        if (seen.has(k)) continue;
        seen.add(k);
        uniq.push(g);
      }
      if (process.env.NODE_ENV !== 'production' && uniq.length !== r.data.games.length) {
        // Teşhis: fark varsa sorun SUNUCU tarafındadır, burada yalnızca maskeleniyor.
        console.warn(
          `[mm2/recent] ${r.data.games.length - uniq.length} yinelenen kayıt atıldı`,
          r.data.games.map((x) => x?.id)
        );
      }
      setRecent(uniq);
    } catch { /* sessiz */ }
  }, []);

  /* ⚠️⚠️ "OTURUM YOK" ile "BAKAMADIM" AYRI ŞEYLERDİR.
     Bu fonksiyon eskiden her hatada null dönüyordu ve çağıran bunu
     "işlem bitti" sayıp pencereyi KAPATIYORDU. Tek bir 429 (hız sınırı) ya
     da anlık ağ titremesi, kullanıcının açık işlemini ekrandan siliyordu:
     çok adımlı bir çekimde 2. adımın linkine bir daha ulaşamıyordu.
     Artık üç durum ayrı: oturum var / oturum yok / bilinmiyor. */
  const loadSession = useCallback(async () => {
    if (!token) return { ok: false, session: null };
    try {
      const r = await axios.get(`${BACKEND_URL}/api/game/mm2/session`, authHeaders);
      const sn = r.data?.session || null;
      setSession(sn);
      return { ok: true, session: sn };
    } catch {
      // Durumu DEĞİŞTİRMEDEN geç: elimizdeki son bilgi, yanlış bir
      // "bitti" varsayımından iyidir.
      return { ok: false, session: null };
    }
  }, [token, authHeaders]);

  const loadGames = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/game/mm2/coinflip/active`);
      setGames(r.data?.games || []);
    } catch { /* sessiz */ }
  }, []);

  /* ⚠️ Config'te SADECE sabitler yok: house_pool da orada ve o DEĞİŞİYOR.
     Havuz her coinflip kesintisinde büyür, her bot oyununda küçülür. Tek
     sefer okunsaydı "Bot" butonu havuz dolduktan sonra da kapalı kalır ve
     kullanıcı sayfayı yenileyene kadar özelliğin açıldığını göremezdi. */
  const loadConfig = useCallback(async () => {
    try {
      const c = await axios.get(`${BACKEND_URL}/api/game/mm2/config`);
      setConfig(c.data || {});
    } catch { /* sessiz — eldeki değerlerle devam */ }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      await loadConfig();
      await Promise.all([loadGames(), loadInventory(), loadTrades(), loadBotStatus(),
                         loadSession(), loadRecent()]);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [loadConfig, loadGames, loadInventory, loadTrades, loadBotStatus, loadSession, loadRecent]);

  /* ⚠️ Havuz DEĞİŞTİĞİNDE tazele — periyodik yoklamayla değil.
     Coinflip sonucu = kesinti alınmış olabilir (havuz büyüdü) ya da bot
     oyunu bitmiş olabilir (havuz değişti). Sunucudaki 8 saniyelik önbellek
     aynı anda biten oyunlarda yığılmayı engelliyor. */
  useEffect(() => {
    if (!lastResultAt) return;
    loadConfig();
  }, [lastResultAt, loadConfig]);

  // ⚠️ HIZ SINIRI BÜTÇESİ. Sunucudaki _mm2_lim_read 60 istek/dakika ve bu
  //    bütçe /inventory, /trades, /coinflip/active, /config, /items uçlarının
  //    HEPSİ tarafından paylaşılıyor. Bekleyen trade varken hem trade hem
  //    envanteri 5 saniyede bir çekmek tek başına 24 istek/dakika ederdi;
  //    kullanıcı biraz gezinince 429 yiyip arayüz donardı.
  //
  //    Çözüm: trade'i 8 saniyede bir yokla (~7.5/dk), envanteri ise SADECE
  //    bir trade'in durumu GERÇEKTEN değiştiğinde tazele. Eşya envantere
  //    zaten durum değişince giriyor; her turda çekmek boşuna istekti.
  const tradeSigRef = useRef('');
  useEffect(() => {
    const sig = trades.map((t) => `${t.id}:${t.status}`).join('|');
    if (sig !== tradeSigRef.current) {
      const first = tradeSigRef.current === '';
      tradeSigRef.current = sig;
      if (!first) loadInventory();
    }
  }, [trades, loadInventory]);

  const pending = useMemo(
    () => trades.some((t) => ['queued', 'sent', 'settling'].includes(t.status)),
    [trades]
  );

  useEffect(() => {
    if (!token) return;
    // ⚠️ Oturum penceresi açıkken trades yoklaması GEREKSİZ: aynı bilgiyi
    //    session ucu zaten 3 saniyede bir getiriyor. İkisini birden yoklamak
    //    okuma sınırını (60/dk) iki sekmede aşıyordu ve 429 alan kullanıcı
    //    işlemini ekranda kaybediyordu.
    const id = setInterval(loadTrades, session ? 30000 : (pending ? 8000 : 30000));
    return () => clearInterval(id);
  }, [pending, token, loadTrades, session]);

  // ⚠️ İşlem AÇIKKEN 3 saniyede bir yoklanır: adım bitince bir sonraki
  //    linkin hemen belirmesi gerekiyor, kullanıcı ekrana bakıyor.
  //    Açık işlem yokken 25 saniye — boşuna istek atmanın anlamı yok.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    const tick = async () => {
      const res = await loadSession();
      if (!alive) return;
      // ⚠️ Yalnızca SUNUCU "oturum yok" dediyse kapat. Sorgu başarısızsa
      //    hiçbir şey yapma — pencere açık kalsın, bir sonraki tur dener.
      if (!res.ok) return;
      const sn = res.session;
      // İşlem BİTTİ → pencereyi kapat ve envanteri tazele (eşyalar geldi).
      if (!sn && sessionOpen) { setSessionOpen(false); loadInventory(); loadTrades(); }
      // ⚠️ Yalnızca YAPILACAK İŞ varken kendiliğinden aç. İncelemedeki bir
      //    çekim de "açık oturum"dur; onun için pencereyi her turda tekrar
      //    açmak, kullanıcının kapatamadığı bir pencere yaratırdı.
      //
      // ⚠️⚠️ CÜZDAN YATIRMASI BU PENCEREYİ AÇMAZ.
      //    Sitede iki ayrı yatırma var ve ikisi de aynı oturum ucundan
      //    görünüyor:
      //      • cüzdan yatırması (mode 'balance') → eşya verir, MM COIN alır
      //      • bu sayfanın yatırması (mode 'items') → eşya verir, EŞYA alır
      //    Kullanıcı cüzdandan MM Coin yatırmaya başlayıp coinflip'e
      //    geçtiğinde, sayfa o işlemi kendi eşya yatırması sanıp üstüne
      //    tam ekran "Deposit in progress" penceresini açıyordu. Lobiye
      //    bakmak, oyun kurmak, envantere göz atmak — hepsi kilitleniyordu,
      //    üstelik o işlemin bu sayfayla hiçbir ilgisi yok.
      //    Artık cüzdan yatırması pencereyi KENDİLİĞİNDEN AÇMIYOR; alt
      //    köşedeki şeritte duruyor ve kullanıcı isterse açıyor.
      if (sn?.actionable && !sessionOpen && !isWalletDepositSession(sn)) {
        setSessionOpen(true);
      }
    };
    tick();
    const id = setInterval(tick, session ? 3000 : 25000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, !!session, sessionOpen, loadSession]);

  // Bot durumu envanter sekmesindeyken tazelenir; coinflip sekmesinde
  // gereksiz istek atmanın anlamı yok (bot orada kullanılmıyor).
  useEffect(() => {
    // Oturum açıkken bot durumu da gereksiz — kullanıcı zaten bir bota atanmış.
    if (tab !== 'inventory' || session) return;
    const id = setInterval(loadBotStatus, 20000);
    return () => clearInterval(id);
  }, [tab, loadBotStatus, session]);

  // WebSocket kopukken lobi listesi tazelenmez (tüm güncellemeler oradan
  // geliyor). Bağlantı yokken 15 saniyede bir HTTP'den çekiyoruz; bağlantı
  // varken HİÇ istek atmıyoruz.
  useEffect(() => {
    if (wsUp) return;
    const id = setInterval(loadGames, 15000);
    return () => clearInterval(id);
  }, [wsUp, loadGames]);

  /* ------------------------------------------------------------- WEBSOCKET */
  useEffect(() => {
    if (!token) return;
    /* ⚠️ YARIŞ: `closedRef` PAYLAŞILAN bir ref idi. Efekt yeniden kurulunca
       (token/meId değişimi) temizlik onu true yapıyor, hemen ardından yeni
       efekt false'a çekiyordu. Eski soketin `onclose`'u bu iki an arasında
       değil de SONRA tetiklenirse bayrak artık false oluyor ve eski zincir
       de yeniden bağlanıyordu → AYNI ANDA İKİ CANLI SOKET, her olay iki kez.
       Çözüm: yaşam bayrağı efekte özel (closure), paylaşılan ref değil. */
    let dead = false;
    closedRef.current = false;
    let timer = null;

    const connect = () => {
      if (dead || closedRef.current) return;
      let ws;
      try {
        ws = new WebSocket(`${WS_URL}/ws/mm2coinflip/${token}`);
      } catch {
        timer = setTimeout(connect, 3000);
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => { setWsUp(true); retryRef.current = 0; };

      ws.onmessage = (ev) => {
        /* ⚠️ BAYAT SOKET: kapanmakta olan ESKİ bağlantıdan gelen gecikmiş
           mesajlar işlenmemeli. Aksi halde aynı MM2_CF_RESULT iki kez
           işlenir; loadRecent/loadInventory iki kez çağrılır ve
           bildirimler çift düşer. */
        if (wsRef.current !== ws) return;
        let d;
        try { d = JSON.parse(ev.data); } catch { return; }

        switch (d.type) {
          case 'MM2_CF_LIST':
            setGames(d.games || []);
            break;

          case 'MM2_CF_CREATED':
            setGames((p) => (p.some((g) => g.id === d.game.id) ? p : [d.game, ...p]));
            break;

          case 'MM2_CF_STARTING': {
            /* ⚠️ LOBİDEN ÇIKARILMAZ — SATIR KALIR VE GERİ SAYAR.
               Önceden oyun anında listeden siliniyordu: katılan kişi
               dışındakiler için coinflip bir anda "yok oluyor" ve ne olduğu
               anlaşılmıyordu. Artık satır yerinde kalıyor, Join butonu gri
               "View"e dönüyor ve geri sayım işliyor — izlemek isteyen
               açabiliyor. Satır, sonuç geldikten sonra temizleniyor. */
            setGames((p) =>
              p.map((g) =>
                g.id === d.game_id
                  ? {
                      ...g,
                      ...(d.game || {}),
                      _startingAt: Date.now(),
                      /* Sunucudan gelen süre esas alınır; sabit 10 yazmak,
                         sunucu ayarı değişince sayacı yalancı yapardı. */
                      _countdown: Number(d.countdown) || 10,
                    }
                  : g
              )
            );
            setLiveFlips((p) => ({
              ...p,
              [d.game_id]: {
                game: d.game,
                phase: 'starting',
                creator_wins: null,
                /* Sayacın kaynağı: lobi satırı ve arena AYNI değerden besleniyor. */
                startedAt: Date.now(),
                countdown: Number(d.countdown) || 10,
              },
            }));
            // Taraflardan biri bensem arenayı KENDİLİĞİNDEN aç.
            if (meId && (d.game?.creator_id === meId || d.game?.joiner_id === meId)) {
              myGamesRef.current.add(d.game_id);
              setFocusId(d.game_id);
            }
            break;
          }

          case 'MM2_CF_FLIPPING':
            setLiveFlips((p) => {
              const cur = p[d.game_id];
              if (!cur) return p;
              return {
                ...p,
                [d.game_id]: {
                  ...cur,
                  phase: 'flipping',
                  creator_wins: d.creator_wins,
                  winner_id: d.winner_id,
                  ticket: d.ticket,
                  ticket_space: d.ticket_space,
                },
              };
            });
            break;

          case 'MM2_CF_RESULT': {
            /* ⚠️ Şerit sonuç GELDİĞİNDE tazelenir, periyodik yoklamayla
               değil. Yoklama hem gecikirdi hem de hiç oyun bitmeyen
               dakikalarda boşuna istek atardı. Sunucu tarafındaki kısa
               önbellek, aynı anda biten oyunlarda yığılmayı da engelliyor. */
            loadRecent();
            setLastResultAt(Date.now());   // havuz değişmiş olabilir → config tazele
            /* Geri sayım bitti, sonuç belli → satır lobiden kalkar. */
            setGames((p) => p.filter((g) => g.id !== d.game_id));
            setLiveFlips((p) => {
              const cur = p[d.game_id];
              if (!cur) return p;
              return {
                ...p,
                [d.game_id]: {
                  ...cur,
                  phase: 'result',
                  creator_wins: d.creator_wins,
                  winner_id: d.winner_id,
                  winner_username: d.winner_username,
                  ticket: d.ticket,
                  ticket_space: d.ticket_space,
                  server_seed: d.server_seed,
                  items_won: d.items_won || [],
                },
              };
            });
            // Eşyalar el değiştirdi → yalnızca BEN taraf isem envanteri tazele.
            if (myGamesRef.current.has(d.game_id)) {
              myGamesRef.current.delete(d.game_id);
              loadInventory();
            }
            // Sonucu bir süre göster, sonra listeden düşür.
            {
              const tid = setTimeout(() => {
                timersRef.current.delete(tid);
                setLiveFlips((p) => {
                  const n = { ...p };
                  delete n[d.game_id];
                  return n;
                });
                setFocusId((f) => (f === d.game_id ? null : f));
              }, 9000);
              timersRef.current.add(tid);
            }
            break;
          }

          case 'MM2_CF_CANCELLED':
            myGamesRef.current.delete(d.game_id);
            setGames((p) => p.filter((g) => g.id !== d.game_id));
            setLiveFlips((p) => {
              const n = { ...p };
              delete n[d.game_id];
              return n;
            });
            setFocusId((f) => (f === d.game_id ? null : f));
            if (d.message) toast.info(d.message);
            loadInventory();
            break;

          default:
            break;
        }
      };

      ws.onerror = () => { try { ws.close(); } catch { /* yoksay */ } };

      ws.onclose = () => {
        if (wsRef.current === ws) setWsUp(false);
        if (dead || closedRef.current) return;
        // Üstel geri çekilme, 15sn tavanlı. Sabit aralıkla yeniden bağlanmak
        // sunucu geçici olarak düştüğünde onu bağlantı seliyle karşılar.
        const wait = Math.min(15000, 1000 * Math.pow(1.7, retryRef.current++));
        timer = setTimeout(connect, wait);
      };
    };

    connect();
    return () => {
      dead = true;
      closedRef.current = true;
      if (timer) clearTimeout(timer);
      try { wsRef.current?.close(); } catch { /* yoksay */ }
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, meId]);

  /* --------------------------------------------------------------- EYLEMLER */
  // Sunucu hata gövdesi {detail: "..."} biçiminde; FastAPI doğrulama
  // hatalarında detail bir DİZİ olabiliyor. İkisini de okunur hale getirir.
  const errText = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d) && d.length) return d[0]?.msg || fallback;
    return fallback;
  };

  const doCreate = async (itemIds, _rbx, side) => {
    setBusy(true);
    try {
      /* side gönderilmezse sunucu id'den türetir — eski davranış korunur. */
      await axios.post(`${BACKEND_URL}/api/game/mm2/coinflip/create`,
        { item_ids: itemIds, ...(side ? { side } : {}) }, authHeaders);
      setModal(null);
      toast.success('Coinflip opened — waiting for an opponent');
      loadInventory();
    } catch (e) {
      toast.error(errText(e, 'Could not open coinflip'));
    } finally { setBusy(false); }
  };

  const doJoin = async (itemIds) => {
    const gameId = modal?.game?.id;
    if (!gameId) return;
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/game/mm2/coinflip/join`,
        { game_id: gameId, item_ids: itemIds }, authHeaders);
      setModal(null);
      loadInventory();
    } catch (e) {
      toast.error(errText(e, 'Could not join'));
    } finally { setBusy(false); }
  };

  const doWithdraw = async (itemIds, rbx) => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/game/mm2/withdraw`,
        { item_ids: itemIds, roblox_username: rbx || '' }, authHeaders);
      setModal(null);
      const n = r.data?.legs?.length || 1;
      toast.success(n > 1
        ? `${r.data?.items} items across ${n} bots — steps will unlock in order`
        : `${r.data?.items || itemIds.length} items queued — join the bot's server and send it a trade request`);
      if (r.data?.skipped_unlocated) {
        toast.error(`${r.data.skipped_unlocated} item(s) have no known bot location and were skipped.`);
      }
      loadInventory();
      loadTrades();
      const { session: sn } = await loadSession();
      if (sn) setSessionOpen(true);
    } catch (e) {
      toast.error(errText(e, 'Could not start withdrawal'));
      loadBotStatus();
    } finally { setBusy(false); }
  };

  const doDeposit = async (rbx) => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/game/mm2/deposit`,
        { roblox_username: rbx || '' }, authHeaders);
      /* ⚠️ BU SAYFANIN YATIRMASI 'items' RAYINDA: eşyalar envantere düşer,
         bakiyeye DEĞİL. Modu kaydediyoruz ki cüzdandan başlatılmış bir
         MM Coin yatırmasıyla karışmasın (bkz. isWalletDepositSession). */
      rememberDepositMode(r.data?.trade_id, r.data?.mode || 'items');
      const b = r.data?.bot;
      toast.success(b?.name
        ? `${b.name} assigned — join its private server and send it a trade request`
        : (r.data?.message || 'Bot assigned — join its server and send it a trade request'));
      // Pencere HEMEN açılsın — kullanıcı linki aramak zorunda kalmasın.
      setDepositOpen(false);
      const { session: sn } = await loadSession();
      if (sn) setSessionOpen(true);
      loadTrades();
    } catch (e) {
      toast.error(errText(e, 'Could not start deposit'));
      // Sunucu "hepsi meşgul / bot yok" dedi → göstergeyi hemen tazele ki
      // kullanıcı butonun neden kapandığını görsün.
      loadBotStatus();
    } finally { setBusy(false); }
  };

  /* ⚠️ useCallback ŞART: bunlar GameRow'a (memo'lu) prop gidiyor.
     Sarmalanmazsa her render'da yeni referans oluşur ve lobideki tüm
     satırlar boşuna yeniden çizilir. */
  const doCancelGame = useCallback(async (gameId) => {
    setBusyId(gameId);
    try {
      await axios.post(`${BACKEND_URL}/api/game/mm2/coinflip/cancel`,
        { game_id: gameId }, authHeaders);
      loadInventory();
    } catch (e) {
      toast.error(errText(e, 'Could not cancel'));
    } finally { setBusyId(null); }
  }, [authHeaders, loadInventory]);

  const doBot = useCallback(async (gameId) => {
    setBusyId(gameId);
    try {
      await axios.post(`${BACKEND_URL}/api/game/mm2/coinflip/bot`,
        { game_id: gameId }, authHeaders);
    } catch (e) {
      toast.error(errText(e, 'Could not call a bot'));
    } finally { setBusyId(null); }
  }, [authHeaders]);

  const doCancelTrade = async (tradeId) => {
    setBusyId(tradeId);
    try {
      await axios.post(`${BACKEND_URL}/api/game/mm2/trade/cancel`,
        { trade_id: tradeId }, authHeaders);
      loadTrades();
      loadInventory();
      loadBotStatus();
      const { ok, session: sn } = await loadSession();
      if (ok && !sn) setSessionOpen(false);
    } catch (e) {
      toast.error(errText(e, 'Could not cancel'));
    } finally { setBusyId(null); }
  };

  /* ------------------------------------------------------------- TÜRETİLEN */
  const liveList = useMemo(() => Object.values(liveFlips), [liveFlips]);
  const sortedGames = useMemo(() => {
    const c = [...games];
    if (lobbySort === 'value') c.sort((a, b) => (b.creator_value || 0) - (a.creator_value || 0));
    else c.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    return c;
  }, [games, lobbySort]);
  const availableCount = useMemo(
    () => inventory.filter((i) => i.state === 'available').length,
    [inventory]
  );
  const shownInventory = useMemo(() => {
    let list = inventory;
    const q = invQuery.trim().toLowerCase();
    if (q) list = list.filter((i) => String(i.name || '').toLowerCase().includes(q));
    const c = [...list];
    if (invSort === 'value_desc') c.sort((a, b) => b.value - a.value);
    else if (invSort === 'value_asc') c.sort((a, b) => a.value - b.value);
    else if (invSort === 'name') c.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    else c.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    // Kilitli eşyalar sona: kullanıcı kullanabileceklerini önce görmeli.
    return c.sort((a, b) => (a.state === 'available' ? 0 : 1) - (b.state === 'available' ? 0 : 1));
  }, [inventory, invQuery, invSort]);

  const maxWithdraw = config?.max_items_per_withdraw ?? 4;

  // Yatırmanın neden yapılamadığını TEK YERDE karar ver; hem buton hem
  // açıklama aynı kaynaktan beslensin (ikisi ayrışırsa kullanıcı kafası karışır).
  const botBlock = useMemo(() => {
    if (!botStat) return null;
    if (botStat.total === 0) return { t: 'Item trading is not available yet', s: 'no bots are configured' };
    if (botStat.online === 0) return { t: 'No bots are online', s: 'please check back in a few minutes' };
    if (botStat.available === 0) return { t: 'All bots are busy', s: 'try again in about a minute' };
    return null;
  }, [botStat]);


  const hasRoblox = !!user?.roblox_username;
  /* ⚠️⚠️ GİRİŞ KAPISI — TEK YERDEN.
     Butonları `disabled` ile kapatmak kullanıcıya NEDEN kapalı olduğunu
     söylemiyordu: tıklıyor, hiçbir şey olmuyor, sebebi anlaşılmıyor.
     Ayrıca deposit/withdraw'da kontrol hiç yoktu — giriş yapmamış biri
     modalı açıp eşya seçmeye başlıyor, ancak istek anında 401 alıyordu.
     Artık her eylem buradan geçiyor: giriş yoksa net bir mesaj, varsa
     eylem çalışıyor. */
  const requireAuth = useCallback((fn) => (...args) => {
    if (!token) {
      toast.error('Sign in to play — your items and coinflips need an account.');
      return;
    }
    fn(...args);
  }, [token]);

  /* ⚠️ KARARLI REFERANSLAR. Bunlar GameRow'a (React.memo) prop olarak
     geçiyor. Satır içi yazılınca her render'da yeni fonksiyon nesnesi
     oluşuyor, memo "prop değişti" sanıyor ve LOBİDEKİ TÜM SATIRLAR
     yeniden çiziliyordu — her WebSocket mesajında, her sayaç turunda.
     useCallback ile referans sabit kalıyor; yalnızca gerçekten değişen
     satır yeniden çiziliyor. */
  const onRowJoin = useCallback(
    requireAuth((game) => setModal({ mode: 'join', game })), [requireAuth]);
  const onRowView = useCallback((id) => setFocusId(id), []);

  const focusLive = focusId ? liveFlips[focusId] : null;
  /* ⚠️ BEKLEYEN oyunlar da açılabilmeli. liveFlips yalnızca DÖNMEKTE olan
     oyunları taşıyor; lobide bekleyen bir oyuna View denince orada karşılığı
     yok. Lobi listesinden bulup aynı ekranı besliyoruz. */
  const focusWaiting = !focusLive && focusId
    ? games.find((g) => g.id === focusId) || null
    : null;

  /* ⚠️ "Açık" = henüz bitmemiş; sunucudaki sayımla AYNI tanım. Yalnızca
     lobide bekleyenleri saymak yanıltıcı olurdu: dönmekte olan oyunlar da
     kullanıcının kotasını tutuyor. */
  const maxOpen = config?.max_open_games ?? 3;
  const myOpenCount = useMemo(() => {
    if (!meId) return 0;
    /* ⚠️ ID'YE GÖRE TEKİLLEŞTİR. Geri sayan bir oyun HEM lobide (satırı
       yerinde kalıyor) HEM liveFlips'te duruyor; ikisini toplamak onu iki
       kez sayıyordu. 2 açık oyunu olan biri, biri dönerken 3 görüp
       butonu kapalı buluyordu. */
    const ids = new Set();
    for (const g of games) if (g.creator_id === meId && g.id) ids.add(g.id);
    for (const l of Object.values(liveFlips)) {
      const g = l?.game;
      if (g?.creator_id === meId && g.id) ids.add(g.id);
    }
    return ids.size;
  }, [games, liveFlips, meId]);
  const atOpenLimit = !!meId && myOpenCount >= maxOpen;

  /* ------------------------------------------------------------------ ÇİZİM */
  return (
    <div className="min-h-screen bg-[#0E0F11] pt-20 sm:pt-24 pb-12">
      <style>{`
        .mm2-scroll { scrollbar-width: none; -webkit-overflow-scrolling: touch; }
        .mm2-scroll::-webkit-scrollbar { height: 0; width: 0; }
      `}</style>

      <div className={`max-w-[1600px] mx-auto px-3 sm:px-4 transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>

        {/* ================= BAŞLIK ================= */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            {/* ⚠️ KILIÇ İKONU YERİNE OYUNUN KENDİ LOGOSU. Lucide'ın genel
                "Swords" simgesi herhangi bir dövüş oyununa ait olabilirdi;
                MM2 oyuncusu logoyu bir metre öteden tanır. Marka, çizilmiş
                bir simgeden daha hızlı okunur. */}
            <Mm2Logo size={38} className="sm:w-11 sm:h-11 drop-shadow-[0_0_16px_rgba(176,89,232,.35)]" />
            <div className="min-w-0">
              <span className="mm-eyebrow">Provably fair · Item wagering</span>
              <h1 className="font-heading font-black text-xl sm:text-2xl md:text-[28px] text-white uppercase tracking-wider leading-none mt-1">
                Murder Mystery 2 <span className="text-[#c98bf0]">Coinflip</span>
              </h1>
            </div>
            {/* ⚠️ SEKME KALDIRILDI — coinflip ve envanter tek sahnede. */}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* ⚠️ "LIVE" ROZETİ KALDIRILDI. Her şey yolundayken bir şeyin
                yolunda olduğunu ilan etmek gürültü; kullanıcı zaten oyunların
                aktığını görüyor. Yalnızca bağlantı KOPTUĞUNDA uyarı çıkıyor —
                bilgi ancak bir şey ters gittiğinde bilgidir. */}
            {!wsUp && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F5A524]/10 border border-[#F5A524]/30">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F5A524] animate-pulse" />
                <span className="text-[11px] font-semibold text-[#F5A524]">Reconnecting</span>
              </div>
            )}
            {/* ⚠️ "INVENTORY" ETİKETİ KALDIRILDI. Çanta ikonu zaten ne
                olduğunu söylüyor; yanına kelimeyi yazmak etiket üstüne
                etiket. Rakam büyütüldü, asıl bilgi o. */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#1A1D20] border border-[#2A2D31]">
              <Package className="w-4 h-4 text-[#B059E8]" />
              <span className="font-heading font-black text-base text-white tabular-nums">
                {fmtVal(invValue)}
              </span>
            </div>
            {games.length > 1 && (
              <div className="flex items-center gap-1 p-1 bg-[#1A1D20] rounded-xl border border-[#2A2D31]">
                {[['value', 'Value'], ['new', 'Newest']].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setLobbySort(k)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${
                      lobbySort === k ? 'bg-[#2A2D31] text-white' : 'text-white/40 hover:text-white/70'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {/* Create, geçmiş şeridinin yanına taşındı — orada göz zaten
                lobiye bakıyor ve buton eylemin olduğu yerde duruyor. */}
          </div>
        </div>

        {/* ================= COINFLIP SEKMESİ ================= */}
        {/* ═══════════════ TEK SAHNE ═══════════════
            ⚠️ Coinflip ve envanter ARTIK YAN YANA. Sekmeliyken oyuncu bir
               oyuna katılmadan önce envanterine bakmak için sahneyi terk
               ediyordu; dönünce lobi değişmiş oluyordu. Tek sahnede karar
               ve kaynak aynı ekranda. Dar ekranda envanter alta iner. */}
        {/* ⚠️ mx-auto + max-w: sohbet kapandığında sahne tam genişliğe yayılıp
            sola yaslanıyordu. Ortalanmış kap, sohbet açık ya da kapalı olsun
            içeriği aynı yerde tutuyor. */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_330px] gap-4 items-start max-w-[1500px] mx-auto">
          <div className="min-w-0">
          <>
            {/* son biten oyunlar — görseldeki "Previous" şeridi */}
            {/* ⚠️ GEÇMİŞ + CREATE AYNI SATIRDA. Create sağ üstte, sayfa
                başlığının yanında kayboluyordu; asıl eylem burada olduğu
                için butonu da buraya aldık. */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 min-w-0">
                <RecentStrip games={recent} />
              </div>
              {/* ⚠️ SINIR ÖNCEDEN GÖSTERİLİR. Sunucu 3 açık coinflip'ten
                  fazlasını reddediyor ama arayüz bunu hiç söylemiyordu:
                  kullanıcı eşyalarını seçip "Open coinflip"e basıyor ve
                  ancak o zaman hata alıyordu. Buton baştan kapalı ve
                  nedeni yazıyor. */}
              <button
                onClick={requireAuth(() => setModal({ mode: 'create' }))}
                title={atOpenLimit
                  ? `You already have ${maxOpen} open coinflips`
                  : undefined}
                disabled={atOpenLimit}
                className="shrink-0 px-4 sm:px-5 py-2.5 rounded-xl bg-[#B059E8] text-white font-heading font-black text-xs uppercase tracking-wider flex items-center gap-2 hover:bg-[#c176f0] hover:-translate-y-0.5 transition-all shadow-[0_10px_26px_-12px_rgba(176,89,232,.9)] disabled:opacity-40 disabled:translate-y-0"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Create coinflip</span>
                <span className="sm:hidden">Create</span>
              </button>
            </div>

            {/* ⚠️ GENİŞ CANLI ŞERİT KALDIRILDI. Aynı coinflip hem lobide
                yatay satır hem üstte geniş kart olarak duruyordu: iki kopya,
                iki görsel dil, dar ekranda gereksiz yükseklik. Dönen oyun
                artık SATIRINDA geri sayıyor ve "View" ile açılıyor. */}

            {/* lobiler */}
            {loading ? (
              <div className="flex flex-col gap-2">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-[70px] rounded-xl bg-[#15171a] border border-[#2A2D31] animate-pulse" />
                ))}
              </div>
            ) : sortedGames.length === 0 ? (
              /* ⚠️ Öğretici metin YOK. Kurallar zaten oluşturma ekranında
                 canlı olarak gösteriliyor; boş lobide anlatmak hem gereksiz
                 hem de ürünü "açıklamaya çalışan" bir şeye çeviriyordu. */
              <div className="rounded-xl border border-dashed border-[#2A2D31] py-10 text-center">
                <p className="text-sm text-white/30">No open coinflips</p>
              </div>
            ) : (
              <motion.div layout className="flex flex-col gap-2">
                <AnimatePresence mode="popLayout">
                  {sortedGames.map((g) => (
                    <GameRow
                      key={g.id}
                      game={g}
                      meId={meId}
                      config={config}
                      busyId={busyId}
                      onJoin={onRowJoin}
                      onCancel={doCancelGame}
                      onBot={doBot}
                      onView={onRowView}
                    />
                  ))}
                </AnimatePresence>
              </motion.div>
            )}

            {/* ⚠️ GEÇMİŞ LOBİNİN ALTINDA. Üstteki nokta şeridi hızlı bir
                "son sonuçlar" özeti; burası ise ayrıntılı, açılabilir liste.
                İkisi aynı veriyi besliyor, ayrı istek atılmıyor. */}
            <FinishedList games={recent} />
          </>
        </div>

          {/* ⚠️ Yapışkan: uzun lobi listesinde kaydırırken envanter ve
              deposit/withdraw ekranda kalmalı. */}
          <div className="xl:sticky xl:top-24 min-w-0">
          <div className="grid grid-cols-1 gap-4">
            {/* --- eşyalar --- */}
            <div className="rounded-2xl border border-[#2A2D31] bg-[#121417] overflow-hidden">
              <div className="px-4 py-3.5 border-b border-[#2A2D31]">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <h2 className="font-heading font-black text-sm text-white uppercase tracking-wider">
                      Your items
                    </h2>
                    <p className="text-[11px] text-white/40 mt-0.5">
                      {availableCount} available
                      {inventory.length - availableCount > 0 &&
                        ` · ${inventory.length - availableCount} locked`}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-heading font-black text-lg text-white leading-none">
                      {fmtVal(invValue)}
                    </p>
                    <p className="text-[10px] text-white/30 mt-1">tradeable value</p>
                  </div>
                </div>

                {inventory.length > 8 && (
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1 min-w-0">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                      <input
                        value={invQuery}
                        onChange={(e) => setInvQuery(e.target.value)}
                        placeholder="Search your items"
                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#0E0F11] border border-[#2A2D31] text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#B059E8]/60 transition-colors"
                      />
                    </div>
                    <select
                      value={invSort} onChange={(e) => setInvSort(e.target.value)}
                      aria-label="Sort items"
                      className="px-3 py-2 rounded-lg bg-[#0E0F11] border border-[#2A2D31] text-xs font-semibold text-white/75 focus:outline-none focus:border-[#B059E8]/60 shrink-0"
                    >
                      <option value="value_desc">Value ↓</option>
                      <option value="value_asc">Value ↑</option>
                      <option value="name">Name</option>
                      <option value="newest">Newest</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="p-4">
                {loading ? (
                  /* ⚠️ SÜTUN SAYISI AZALTILDI: 6 yerine 4. Dar kutularda ad
               sığmıyor ve görsel tanınmıyordu. Az sütun, geniş kutu. */
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                    {Array.from({ length: 12 }, (_, i) => (
                      <div key={i} className="h-[104px] rounded-xl bg-[#15171a] border border-[#2A2D31] animate-pulse" />
                    ))}
                  </div>
                ) : inventory.length === 0 ? (
                  <div className="py-14 text-center">
                    <Package className="w-9 h-9 mx-auto mb-3 text-white/15" />
                    <p className="text-sm text-white/40">No items yet</p>
                    <p className="text-sm text-white/40 mb-5">
                      
                    </p>
                    <button
                      onClick={requireAuth(() => setDepositOpen(true))}
                      disabled={busy || !!botBlock}
                      className="px-5 py-2.5 rounded-xl bg-[#16A34A] text-white font-heading font-black text-xs uppercase tracking-wider inline-flex items-center gap-2 hover:bg-[#15913f] transition-colors disabled:opacity-40"
                    >
                      <ArrowDownToLine className="w-4 h-4" /> Deposit items
                    </button>
                  </div>
                ) : (
                  shownInventory.length === 0 ? (
                    <div className="py-12 text-center">
                      <Search className="w-7 h-7 mx-auto mb-2 text-white/15" />
                      <p className="text-sm text-white/45">No items match "{invQuery}"</p>
                    </div>
                  ) : (
                    /* ⚠️ SÜTUN SAYISI AZALTILDI: 6 yerine 4. Dar kutularda ad
               sığmıyor ve görsel tanınmıyordu. Az sütun, geniş kutu. */
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                      {shownInventory.map((it) => (
                        <ItemTile key={it.id} item={it} showState compact />
                      ))}
                    </div>
                  )
                )}
              </div>
            </div>

            {/* --- yan panel --- */}
            <div className="space-y-4">
              {/* yatır / çek */}
              {/* ⚠️ "MOVE ITEMS" BAŞLIĞI KALDIRILDI. Altında zaten "Deposit"
                  ve "Withdraw" butonları var; başlık ikisinin ne yaptığını
                  tekrar ediyordu. Butonlar kendini anlatıyor. */}
              <div className="rounded-2xl border border-[#2A2D31] bg-[#121417] p-4 space-y-2.5">

                {/* ⚠️ "BOTS 1/1 ready" KALDIRILDI. Bot sayısı işletme
                    ayrıntısı; oyuncunun bilmesi gereken tek şey deposit'in
                    şu an çalışıp çalışmadığı. Çalışmıyorsa zaten aşağıda
                    botBlock ile nedeni yazıyor. */}

                <button
                  onClick={requireAuth(() => setDepositOpen(true))}
                  disabled={busy || !!botBlock || !!session}
                  className="mm-focus w-full px-4 py-3 rounded-xl bg-[#16A34A] text-white font-heading font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#15913f] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownToLine className="w-4 h-4" />}
                  Deposit
                </button>

                {/* ══════════════════════════════════════════════════════
                    ⚠️ AÇIK BİR TAKAS VARKEN NEDEN YATIRAMADIĞINI SÖYLE.
                    Sunucu aynı anda tek takasa izin veriyor. Buton eskiden
                    tıklanabilir kalıyor, istek gidiyor ve kullanıcı
                    "You already have a trade in progress" hatasını ancak
                    sunucudan öğreniyordu. Üstelik o takas ÇOĞU ZAMAN
                    cüzdandan başlatılmış bir MM Coin yatırmasıydı ve
                    kullanıcı onun burayı neden etkilediğini anlamıyordu.
                    Artık sebep önceden yazıyor ve tek tıkla o işleme
                    gidiliyor — bitir ya da iptal et, sonra devam.
                    ══════════════════════════════════════════════════════ */}
                {session ? (
                  <button type="button" onClick={() => setSessionOpen(true)}
                    className="mm-focus w-full text-left px-3 py-2.5 rounded-xl bg-[#1A1D20]
                               border border-[#2A2D31] hover:border-[#B059E8]/50 transition-colors">
                    <p className="text-[11px] text-[#FFCB6B] leading-relaxed">
                      <span className="font-bold">
                        {isWalletDepositSession(session)
                          ? 'A wallet deposit is running'
                          : 'A trade is already running'}
                      </span>
                      {' — '}
                      {isWalletDepositSession(session)
                        ? 'that one credits MM Coin, not items. Finish or cancel it to deposit here.'
                        : 'finish it before starting another.'}
                    </p>
                    <span className="text-[10px] font-bold text-[#c98bf0] mt-1 inline-block">
                      Open it →
                    </span>
                  </button>
                ) : botBlock && (
                  <p className="text-[11px] text-[#FFCB6B] leading-relaxed">
                    <span className="font-bold">{botBlock.t}</span> — {botBlock.s}.
                  </p>
                )}


                <button
                  onClick={requireAuth(() => setModal({ mode: 'withdraw' }))}
                  /* ⚠️ Giriş yapmamış kullanıcıda envanter boş görünür ve
                     buton "eşya yok" diye kapanırdı — asıl sebep giriş
                     olmaması. Boşluk kontrolü YALNIZCA girişliyken geçerli;
                     girişsizken buton tıklanabilir kalıp mesaj veriyor. */
                  disabled={busy || !!session || (!!token && availableCount === 0)}
                  className="mm-focus w-full px-4 py-3 rounded-xl bg-[#1A1D20] border border-[#2A2D31] text-white font-heading font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 hover:border-[#B059E8]/50 transition-colors disabled:opacity-40"
                >
                  <ArrowUpFromLine className="w-4 h-4 text-[#B059E8]" />
                  Withdraw
                </button>

                {/* ⚠️ KURAL LİSTESİ KALDIRILDI ("Withdraw limit / Coinflip
                    range / House cut"). Kimsenin sormadığı sabitleri panelde
                    saymak ürünü bir dokümantasyon sayfasına çeviriyordu.
                    Bu değerler zaten ihtiyaç duyulduğu ANDA görünüyor:
                    çekim sınırı çekim ekranında, katılım aralığı katılım
                    ekranında yazıyor. */}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* ================= KATMANLAR ================= */}

      {/* İşlem penceresi — kullanıcının odak noktası */}
      <AnimatePresence>
        {sessionOpen && session && (
          <TradeSession
            session={session}
            busyId={busyId}
            onCancel={doCancelTrade}
            onClose={() => setSessionOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* ⚠️ Pencereyi kapatan kullanıcı işlemi KAYBETMİŞ hissetmemeli.
          İşlem devam ederken sabit bir şerit durur ve tek dokunuşla geri açar. */}
      <AnimatePresence>
        {session && !sessionOpen && (
          <motion.button
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            onClick={() => setSessionOpen(true)}
            className={`fixed bottom-4 right-4 z-[75] px-4 py-3 rounded-2xl text-white flex items-center gap-2.5 transition-colors ${
              session.needs_review
                ? 'bg-[#F5A524] hover:bg-[#f7b955] shadow-[0_16px_40px_-12px_rgba(245,165,36,.9)]'
                : 'bg-[#B059E8] hover:bg-[#c176f0] shadow-[0_16px_40px_-12px_rgba(176,89,232,.9)]'
            }`}
          >
            {session.needs_review
              ? <ShieldCheck className="w-4 h-4" />
              : <Loader2 className="w-4 h-4 animate-spin" />}
            <div className="text-left">
              <p className="font-heading font-black text-xs uppercase tracking-wider leading-none">
                {session.needs_review
                  ? 'Under review'
                  : session.type !== 'deposit' ? 'Withdrawal in progress'
                  : isWalletDepositSession(session) ? 'Wallet deposit in progress'
                  : 'Item deposit in progress'}
              </p>
              {session.total_steps > 1 && (
                <p className="text-[10px] opacity-80 mt-0.5">
                  Step {(session.active_step?.leg_index ?? 0) + 1} of {session.total_steps}
                </p>
              )}
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {focusWaiting && (
          <ResultModal
            key={`w-${focusId}`}
            waiting
            /* ⚠️ Giriş kontrolü BURADA DEĞİL. Girişsiz kullanıcıda meId boş
               olduğu için buton "Waiting" diye kapanıyordu — oysa sebep
               giriş olmaması. Buton yalnızca KENDİ oyununda kapalı; giriş
               kontrolü onJoin içindeki requireAuth'ta ve orada mesaj var. */
            canJoin={!meId || focusWaiting.creator_id !== meId}
            onJoin={requireAuth(() => { setFocusId(null); setModal({ mode: 'join', game: focusWaiting }); })}
            game={{
              id: focusWaiting.id,
              creator_side: focusWaiting.creator_side,
              creator: focusWaiting.creator_username,
              joiner: null,
              creator_value: focusWaiting.creator_value || 0,
              joiner_value: 0,
              creator_chance: 50,
              creator_items: focusWaiting.creator_items,
              joiner_items: null,
              creator_won: null,
              vs_bot: false,
              pot: focusWaiting.creator_value || 0,
              at: null,
            }}
            onClose={() => setFocusId(null)}
          />
        )}
        {focusLive && (
          /* ⚠️ Canlı oyun da GEÇMİŞLE AYNI ekranda. İki ayrı bileşen tutmak
             ikisinin zamanla ayrışmasına yol açıyordu — nitekim açmıştı. */
          <ResultModal
            key={focusId}
            live={focusLive}
            game={{
              id: focusLive.game?.id,
              creator_side: focusLive.game?.creator_side,
              creator: focusLive.game?.creator_username,
              joiner: focusLive.game?.joiner_username || '—',
              creator_value: focusLive.game?.creator_value || 0,
              joiner_value: focusLive.game?.joiner_value || 0,
              creator_chance: focusLive.game?.creator_chance ?? 50,
              creator_items: focusLive.game?.creator_items,
              joiner_items: focusLive.game?.joiner_items,
              creator_won: focusLive.creator_wins,
              vs_bot: focusLive.game?.joiner_is_bot,
              pot: (focusLive.game?.creator_value || 0) + (focusLive.game?.joiner_value || 0),
              at: null,
            }}
            onClose={() => setFocusId(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {depositOpen && (
          <DepositModal
            open
            profileName={user?.roblox_username || ''}
            busy={busy}
            onClose={() => !busy && setDepositOpen(false)}
            onStart={doDeposit}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {modal && (
          <ItemPickerModal
            key={modal.mode + (modal.game?.id || '')}
            open
            mode={modal.mode}
            game={modal.game}
            config={config}
            inventory={inventory}
            busy={busy}
            profileName={user?.roblox_username || ''}
            onClose={() => !busy && setModal(null)}
            onSubmit={
              modal.mode === 'create' ? doCreate
                : modal.mode === 'join' ? doJoin
                : doWithdraw
            }
          />
        )}
      </AnimatePresence>
    </div>
  );
};